import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  GraduationCap,
  Scale,
  Sparkles,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Copy,
  Check,
  RotateCcw,
  Send,
  Loader2,
  ShieldCheck,
  ChevronRight,
  ClipboardList,
  Flame,
  ArrowRight,
  HelpCircle,
  Clock,
  Printer,
  FileCheck,
  Download,
  ShieldAlert,
  SearchCheck,
  Briefcase,
  Calendar,
  AlertOctagon,
  Eye,
  FileWarning,
  Search,
  Building2,
  UserCheck,
  Inbox,
  Users,
  Award,
  Mail,
  FileEdit,
  ScrollText,
  PenTool,
  UserPlus,
  CalendarPlus,
  Timer
} from "lucide-react";
import SignaturePad from "signature_pad";
import { FESTIVOS_COLOMBIA } from "@/lib/calculators/colombiaData";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API_BASE_URL } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { useDespachos } from "@/hooks/useDespachos";
import { searchJudicial, type JudicialSearchResult } from "@/services/scraperApi";
import { ExpedienteClinicoModal } from "@/components/campus/ExpedienteClinicoModal";
import { createAuthHeaders } from "@/lib/auth-utils";
import { asBlob } from "html-docx-js-typescript";
import { saveAs } from "file-saver";
import * as mammoth from "mammoth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

// Filtro protector para suprimir preguntas de seguimiento en la suite universitaria
const stripFollowUpQuestions = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.split(/(?:\r?\n|^)\s*#{1,4}\s*preguntas\s+de\s+seguimiento\b/i)[0].trim();
};

// Modelos de ejemplo precargados para demostración ágil
const SOCRATIC_EXAMPLES = [
  {
    title: "Simulación Contractual y Prescripción",
    matter: "Derecho Civil / Obligaciones",
    issue: "¿Es imprescriptible la acción de prevalencia en la simulación absoluta o está sujeta al término decenal del art. 2536 del Código Civil?",
    sources: "Art. 1766 y 2536 Código Civil; Corte Suprema de Justicia, Sala de Casación Civil, Sentencia SC2582-2020",
    facts: "Un deudor vende simuladamente su único bien inmueble a su hermano mediante escritura pública para evitar el embargo de un pagaré vencido. Tras 11 años, el acreedor descubre el acto ficticio y desea demandar.",
    studentThesis: "Mi tesis es que en la simulación absoluta la acción de nulidad es imprescriptible porque el acto nunca existió realmente, por lo que el acreedor puede demandar en cualquier tiempo según el art. 1766 del Código Civil.",
  },
  {
    title: "Estabilidad Laboral Reforzada en Despido",
    matter: "Derecho Laboral Individual",
    issue: "¿Es eficaz la terminación unilateral sin justa causa con indemnización (Art. 64 CST) de un trabajador con afectación de salud conocida por el empleador sin autorización del Inspector de Trabajo?",
    sources: "Art. 26 Ley 361 de 1997; Art. 64 CST; Corte Constitucional SU-049 de 2017 y SU-087 de 2022",
    facts: "Trabajador con contrato a término indefinido sufre accidente común con incapacidad continua de 45 días. Al regresar a sus labores, el empleador le notifica terminación unilateral pagando la indemnización del Art. 64 CST.",
    studentThesis: "Considero que el despido es plenamente eficaz porque la empresa pagó la indemnización legal por despido sin justa causa, cumpliendo la obligación pecuniaria del CST.",
  },
  {
    title: "Nulidad Procesal vs. Ineficacia Probatoria",
    matter: "Derecho Procesal General (CGP)",
    issue: "¿La falta de traslado previo de un dictamen pericial configura causal taxativa de nulidad procesal (Art. 133 CGP) o irregularidad en la contradicción de la prueba?",
    sources: "Arts. 133, 228 y 231 del Código General del Proceso (Ley 1564 de 2012); Art. 29 Constitución Política",
    facts: "En un proceso verbal sumario se practicó un dictamen pericial sin correr traslado a la contraparte para contradicción antes de la audiencia de instrucción y juzgamiento.",
    studentThesis: "El juez debe decretar de oficio la nulidad procesal absoluta de todo lo actuado desde la admisión de la demanda por violación al debido proceso.",
  }
];

const CLINIC_EXAMPLES = [
  {
    title: "Despido de Trabajador sin Contrato Escrito",
    category: "Laboral",
    claimant: "Carlos Mario Restrepo",
    userDoc: "C.C. 1.020.455.891",
    estrato: 1,
    vulnerability: "Trabajador Informal / Desempleado",
    counterparty: "Comercializadora El Mayorista S.A.S. (NIT 900.412.889-1)",
    incidentDate: "2026-10-15",
    proceduralReq: "Pendiente agotar conciliación / reclamación",
    evidences: ["Cédula / PPT", "Desprendibles / Extractos", "Capturas WhatsApp / Correos"],
    amount: "14.5 SMLMV",
    summary: "El usuario laboró 2 años como auxiliar de bodega con pago quincenal en efectivo sin afiliación a seguridad social. Fue despedido verbalmente sin liquidación ni preaviso tras reclamar dotación.",
  },
  {
    title: "Fijación de Alimentos y Custodia de Menor",
    category: "Familia",
    claimant: "Laura Patricia Gómez",
    userDoc: "C.C. 52.891.204",
    estrato: 2,
    vulnerability: "Madre/Padre Cabeza de Familia",
    counterparty: "Andrés Felipe Castaño (C.C. 80.112.904)",
    incidentDate: "2026-05-01",
    proceduralReq: "Pendiente agotar conciliación extrajudicial",
    evidences: ["Cédula / PPT", "Registro Civil / Parentesco", "Capturas WhatsApp / Correos"],
    amount: "1 SMLMV",
    summary: "Madre cabeza de familia busca fijar cuota alimentaria para su hijo de 4 años. El padre labora como conductor de servicio público y no aporta desde hace 6 meses. No hay acuerdo previo.",
  },
  {
    title: "Negativa de Medicamento e Insumos en EPS",
    category: "Constitucional",
    claimant: "Gladys Elena Morales",
    userDoc: "C.C. 20.415.772",
    estrato: 2,
    vulnerability: "Adulto Mayor (+60 años)",
    counterparty: "Nueva EPS S.A.",
    incidentDate: "2026-10-20",
    proceduralReq: "No aplica / Acción de Tutela",
    evidences: ["Cédula / PPT", "Historia Clínica / Incapacidades", "Fórmula Médica Vigente"],
    amount: "No aplica",
    summary: "EPS niega suministro de insulina análoga prescrita por médico tratante aduciendo falta de cobertura en el plan básico, poniendo en riesgo vital a paciente diabética de 74 años.",
  }
];

const VULNERABILITY_OPTIONS = [
  "Sujeto de bajos recursos (Estrato 1, 2 o 3)",
  "Madre/Padre Cabeza de Familia",
  "Víctima del Conflicto Armado (RUV)",
  "Persona con Discapacidad / Estabilidad Reforzada",
  "Adulto Mayor (+60 años)",
  "Población Migrante / Refugiada (PPT / PEP)",
  "Trabajador Informal / Desempleado",
  "Comunidad Étnica / Campesina",
];

const EVIDENCE_CHECKLIST_OPTIONS = [
  "Cédula / PPT",
  "Contrato / Certificación",
  "Historia Clínica / Incapacidades",
  "Desprendibles / Extractos",
  "Carta de Despido / Respuesta",
  "Derecho de Petición Previo",
  "Capturas WhatsApp / Correos",
  "Fórmula Médica Vigente",
  "Registro Civil / Parentesco",
];

function parseCopAmount(raw: string): number {
  if (!raw) return 0;
  const normalized = raw.toLowerCase().trim();
  if (normalized.includes("no aplica") || normalized.includes("derecho fundamental") || normalized === "0") {
    return 0;
  }
  if (normalized.includes("smlmv") || normalized.includes("smmlv")) {
    const numMatch = normalized.replace(",", ".").match(/(\d+(\.\d+)?)/);
    const smlmvCount = numMatch ? parseFloat(numMatch[1]) : 0;
    return Math.round(smlmvCount * 1550000);
  }
  const digitsOnly = normalized.replace(/[^\d]/g, "");
  return digitsOnly ? Number(digitsOnly) : 0;
}

const CITATION_AUDIT_EXAMPLES = [
  {
    title: "Ensayo sobre Acción de Tutela contra Providencias",
    text: "Como señaló la Corte Constitucional en la Sentencia T-999 de 2024, 'la tutela contra sentencias judiciales procede de plano sin verificar requisitos de subsidiariedad ni inmediatez si se alega defecto fáctico'. Asimismo, el Art. 86 de la Constitución y el Decreto 2591 de 1991 exigen que el accionante agote todos los recursos ordinarios salvo perjuicio irremediable.",
  },
  {
    title: "Memorial de Recurso de Apelación Laboral",
    text: "En virtud del Art. 64 del Código Sustantivo del Trabajo, el despido con justa causa genera indemnización moratoria automática. Además, la Sala de Casación Laboral en Sentencia SL-88899 de 2025 estableció que no se requiere demostrar culpa patronal para la sanción del artículo 65 del CST.",
  }
];

// Casos Clínicos Precargados para Demostración en Vivo Universitaria
const DEMO_CLINIC_PROCESSES = [
  {
    id: "demo-case-1",
    case_number: "11001400302520260012300",
    radicado: "11001400302520260012300",
    title: "Graciela López Gil vs. Administradora de Fondos Porvenir",
    parties: "Graciela López Gil vs. Administradora de Fondos Porvenir",
    despacho: "Juzgado 025 Civil Municipal de Bogotá",
    status: "active",
    last_action_date: "2026-02-25",
    tags: ["Consultorio Jurídico", "Ley 2113", "Estudiante: Juan Camilo Pérez"],
    metadata: { is_clinic: true, student_assigned: "Juan Camilo Pérez (Semestre X)", vacation_watch: true }
  },
  {
    id: "demo-case-2",
    case_number: "11001400302520260045600",
    radicado: "11001400302520260045600",
    title: "Carlos Eduardo Restrepo vs. Distribuciones del Valle S.A.S.",
    parties: "Carlos Eduardo Restrepo vs. Distribuciones del Valle S.A.S.",
    despacho: "Juzgado 012 Laboral del Circuito de Bogotá",
    status: "active",
    last_action_date: "2026-02-18",
    tags: ["Consultorio Jurídico", "Ley 2113", "Estudiante: Valentina Arismendi"],
    metadata: { is_clinic: true, student_assigned: "Valentina Arismendi (Semestre IX)", vacation_watch: true }
  },
  {
    id: "demo-case-3",
    case_number: "11001400302520260089100",
    radicado: "11001400302520260089100",
    title: "Marta Lucía Gómez vs. Seguros Sociales en Liquidación",
    parties: "Marta Lucía Gómez vs. Seguros Sociales en Liquidación",
    despacho: "Juzgado 004 de Pequeñas Causas y Competencia Múltiple",
    status: "active",
    last_action_date: "2026-02-10",
    tags: ["Consultorio Jurídico", "Ley 2113", "Estudiante: Mateo Cárdenas"],
    metadata: { is_clinic: true, student_assigned: "Mateo Cárdenas (Semestre X)", vacation_watch: true }
  }
];

export interface CampusConsultorioProps {
  moduleFocus?: "campus" | "consultorio";
}

export default function CampusConsultorioWorkstation({ moduleFocus }: CampusConsultorioProps = {}) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const isAdmin = profile?.role === "admin" || (user as any)?.user_metadata?.role === "admin";
  const { processes, loading: loadingProcesses, addProcess } = useDespachos();
  const [searchParams, setSearchParams] = useSearchParams();

  // Rol Institucional Activo (Estudiante, Docente Tutor, Director de Consultorio)
  type CampusRole = "estudiante" | "docente_tutor" | "director_consultorio";
  const initialRole: CampusRole = (searchParams.get("role") as CampusRole) ||
    (moduleFocus === "campus" ? "estudiante" : undefined) ||
    (localStorage.getItem("campus_active_role") as CampusRole) ||
    (searchParams.get("view") === "docente" ? "docente_tutor" : "estudiante");
  const [activeRole, setActiveRole] = useState<CampusRole>(initialRole);

  // Navegación Unificada de Campus mediante Pestañas de Primer Nivel adaptadas por Rol
  type CampusTab = "clinic" | "socratic" | "citations" | "despachos" | "docente" | "director_dashboard" | "practicantes";
  const initialTab: CampusTab = (searchParams.get("view") === "docente" || searchParams.get("tab") === "docente")
    ? "docente"
    : moduleFocus === "campus"
      ? "socratic"
      : moduleFocus === "consultorio"
        ? (initialRole === "director_consultorio" ? "director_dashboard" : (initialRole === "docente_tutor" ? "docente" : "clinic"))
        : (searchParams.get("tab") as CampusTab) || (initialRole === "director_consultorio" ? "director_dashboard" : (initialRole === "docente_tutor" ? "docente" : "clinic"));
  const [activeTab, setActiveTab] = useState<CampusTab>(initialTab);

  const handleRoleChange = (role: CampusRole) => {
    setActiveRole(role);
    localStorage.setItem("campus_active_role", role);
    const next = new URLSearchParams(searchParams);
    next.set("role", role);
    if (role === "docente_tutor") {
      setActiveTab("docente");
      next.set("tab", "docente");
      next.set("view", "docente");
    } else if (role === "director_consultorio") {
      setActiveTab("director_dashboard");
      next.set("tab", "director_dashboard");
      next.set("view", "director");
    } else {
      setActiveTab("clinic");
      next.set("tab", "clinic");
      next.set("view", "estudiante");
    }
    setSearchParams(next);
  };

  const handleTabChange = (tab: CampusTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (tab === "docente") {
      next.set("view", "docente");
    } else if (tab === "director_dashboard" || tab === "practicantes") {
      next.set("view", "director");
    } else {
      next.set("view", "estudiante");
    }
    setSearchParams(next);
  };
  const currentView = activeTab === "docente" ? "docente" : (activeTab === "director_dashboard" || activeTab === "practicantes" ? "director" : "estudiante");
  const activeStation = activeTab === "docente" ? "clinic" : (activeTab === "director_dashboard" || activeTab === "practicantes" ? "clinic" : activeTab);
  const setActiveStation = (station: "socratic" | "clinic" | "citations" | "despachos") => {
    handleTabChange(station);
  };
  const setView = (view: "estudiante" | "docente") => {
    if (view === "docente") {
      handleTabChange("docente");
    } else {
      handleTabChange("clinic");
    }
  };
  const [demoModeActive, setDemoModeActive] = useState(false);

  // Registro Persistente de Casos Clínicos (Local-First + Backend)
  const [savedCases, setSavedCases] = useState<any[]>(() => {
    try {
      const local = localStorage.getItem("campus_saved_cases_v1");
      return local ? JSON.parse(local) : [];
    } catch {
      return [];
    }
  });
  const [activeCaseId, setActiveCaseId] = useState<string | null>(() => {
    return localStorage.getItem("campus_active_case_id") || null;
  });

  // Sincronizar casos guardados con localStorage
  useEffect(() => {
    try {
      localStorage.setItem("campus_saved_cases_v1", JSON.stringify(savedCases));
    } catch (e) {
      console.error("Error al persistir casos en storage:", e);
    }
  }, [savedCases]);

  // Escuchar aprobación de Vo.Bo. desde el modal para sincronizar bandeja docente y casos
  useEffect(() => {
    const handleCaseApproved = () => {
      try {
        const local = localStorage.getItem("campus_saved_cases_v1");
        if (local) setSavedCases(JSON.parse(local));
      } catch (e) {
        console.error(e);
      }
    };
    window.addEventListener("campus_case_approved", handleCaseApproved);
    return () => window.removeEventListener("campus_case_approved", handleCaseApproved);
  }, []);

  // Combinar casos persistidos con procesos judiciales monitoreados
  const displayProcesses = [
    ...savedCases,
    ...((processes && processes.length > 0 && !demoModeActive) ? processes : DEMO_CLINIC_PROCESSES).filter(
      (p: any) => !savedCases.some((sc: any) => sc.radicado === p.radicado || sc.id === p.id)
    )
  ];

  // Guardar o actualizar un caso en el almacén persistente
  const persistCaseRecord = async (newCaseData: any) => {
    setSavedCases((prev) => {
      const existsIndex = prev.findIndex((c) => (c.id && c.id === newCaseData.id) || (c.radicado && c.radicado === newCaseData.radicado));
      if (existsIndex >= 0) {
        const updated = [...prev];
        updated[existsIndex] = {
          ...updated[existsIndex],
          ...newCaseData,
          metadata: {
            ...(updated[existsIndex].metadata || {}),
            ...(newCaseData.metadata || {})
          },
          updated_at: new Date().toISOString()
        };
        return updated;
      }
      return [newCaseData, ...prev];
    });

    // Guardado no-bloqueante en backend
    try {
      const authHeaders = await createAuthHeaders();
      await fetch(`${API_BASE_URL}/api/v1/campus/cases`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(newCaseData)
      });
    } catch (err) {
      console.warn("Sincronización en backend pendiente, caso preservado en almacenamiento local.", err);
    }
  };

  // Registro de Usuarios del Consultorio (Estudiantes y Profesores)
  const DEFAULT_CAMPUS_USERS = [
    { id: "usr-1", full_name: "Juan Camilo Pérez", email: "juan.perez@universidad.edu.co", role: "estudiante", area: "Laboral", semestre: "Semestre X", horas_practica: 14, cases: 3, score: "4.9" },
    { id: "usr-2", full_name: "Valentina Arismendi", email: "valentina.arismendi@universidad.edu.co", role: "estudiante", area: "Civil", semestre: "Semestre IX", horas_practica: 10, cases: 2, score: "4.8" },
    { id: "usr-3", full_name: "Mateo Cárdenas", email: "mateo.cardenas@universidad.edu.co", role: "estudiante", area: "Laboral", semestre: "Semestre X", horas_practica: 18, cases: 4, score: "4.7" },
    { id: "usr-4", full_name: "Dra. Helena Morales", email: "helena.morales@universidad.edu.co", role: "docente_tutor", area: "Laboral", tarjeta_profesional: "T.P. 182.934 CSJ", cases: 9 }
  ];
  const [campusUsers, setCampusUsers] = useState<any[]>(() => {
    try {
      const local = localStorage.getItem("campus_registered_users_v1");
      return local ? JSON.parse(local) : DEFAULT_CAMPUS_USERS;
    } catch {
      return DEFAULT_CAMPUS_USERS;
    }
  });
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserDocId, setNewUserDocId] = useState("");
  const [newUserRole, setNewUserRole] = useState<"estudiante" | "docente_tutor" | "director_consultorio">("estudiante");
  const [newUserArea, setNewUserArea] = useState("Laboral");
  const [newUserSemestre, setNewUserSemestre] = useState("Consultorio III (Semestre IX)");
  const [newUserTP, setNewUserTP] = useState("");
  const [newUserAssignedTutor, setNewUserAssignedTutor] = useState("Dra. Helena Morales");
  const [isRegisteringUser, setIsRegisteringUser] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("campus_registered_users_v1", JSON.stringify(campusUsers));
    } catch (e) {
      console.error("Error guardando usuarios en local:", e);
    }
  }, [campusUsers]);

  const handleSaveCampusUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) {
      toast.error("Por favor ingresa el nombre y correo institucional");
      return;
    }
    setIsRegisteringUser(true);
    try {
      const newUserPayload = {
        id: `usr-${Date.now()}`,
        full_name: newUserName.trim(),
        email: newUserEmail.trim(),
        role: newUserRole,
        area: newUserArea,
        semestre: newUserRole === "estudiante" ? newUserSemestre : undefined,
        tarjeta_profesional: newUserRole !== "estudiante" ? newUserTP.trim() : undefined,
        horas_practica: newUserRole === "estudiante" ? 0 : undefined,
        cases: 0,
        score: "5.0",
        metadata: {
          documento_codigo: newUserDocId.trim() || undefined,
          docente_tutor_asignado: newUserRole === "estudiante" ? newUserAssignedTutor : undefined,
          registered_under: "Ley 2113 de 2021 / SICAJ"
        }
      };

      setCampusUsers(prev => [newUserPayload, ...prev]);

      try {
        const authHeaders = await createAuthHeaders();
        await fetch(`${API_BASE_URL}/api/v1/campus/users`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify(newUserPayload)
        });
      } catch (e) {
        console.warn("Backend user registration pending, saved in local storage:", e);
      }

      const roleLabel =
        newUserRole === "estudiante"
          ? "Estudiante Practicante"
          : newUserRole === "docente_tutor"
            ? "Docente Asesor"
            : "Director(a) de Consultorio";
      toast.success(`${roleLabel} matriculado con éxito en el censo SICAJ`);
      setIsUserModalOpen(false);
      setNewUserName("");
      setNewUserEmail("");
      setNewUserDocId("");
      setNewUserTP("");
    } catch (err: any) {
      console.error("Error al registrar usuario:", err);
      toast.error("No se pudo completar el registro");
    } finally {
      setIsRegisteringUser(false);
    }
  };

  // Estado Modal Vincular Radicado para Consultorio
  const [isRadicadoModalOpen, setIsRadicadoModalOpen] = useState(false);
  const [newRadicadoNum, setNewRadicadoNum] = useState("");
  const [newRadicadoTitle, setNewRadicadoTitle] = useState("");
  const [newRadicadoStudent, setNewRadicadoStudent] = useState("");
  const [addingRadicado, setAddingRadicado] = useState(false);
  const [isSearchingJudicial, setIsSearchingJudicial] = useState(false);
  const [judicialPreview, setJudicialPreview] = useState<JudicialSearchResult | null>(null);
  const [judicialSearchError, setJudicialSearchError] = useState<string | null>(null);
  const [selectedProcessForExpediente, setSelectedProcessForExpediente] = useState<any | null>(null);

  // Auto-apertura si viene deep-link de correo (?open=expediente&radicado=...)
  useEffect(() => {
    const targetRadicado = searchParams.get("radicado");
    if (targetRadicado) {
      if (processes && processes.length > 0) {
        const matched = processes.find(
          (p: any) => p.radicado === targetRadicado || p.case_number === targetRadicado
        );
        if (matched) {
          setSelectedProcessForExpediente(matched);
          return;
        }
      }
      // Si aún no está en cache de procesos, abrir expediente sintético para el radicado del correo
      setSelectedProcessForExpediente({
        radicado: targetRadicado,
        case_number: targetRadicado,
        despacho: "Juzgado 025 Civil Municipal de Bogotá",
        parties: `Caso en Revisión — Rad. ${targetRadicado.slice(-6)}`,
        status: "active",
      });
    }
  }, [searchParams, processes]);

  // Consultar información oficial en Rama Judicial antes de vincular
  const handleVerifyRadicado = async () => {
    const cleanRadicado = newRadicadoNum.replace(/\s+/g, "").trim();
    if (!cleanRadicado || cleanRadicado.length < 12) {
      toast.error("Por favor ingresa un número de radicado válido (mínimo 12 o 23 dígitos)");
      return;
    }
    setIsSearchingJudicial(true);
    setJudicialSearchError(null);
    setJudicialPreview(null);
    try {
      const res = await searchJudicial({
        query: cleanRadicado,
        type: "radicado",
        sources: ["cpnu"],
      });

      if (res && res.results && res.results.length > 0) {
        const match = res.results[0];
        setJudicialPreview(match);
        // Auto-poblar carátula con las partes oficiales
        const demandante = typeof match.demandante === "string" ? match.demandante : "";
        const demandado = typeof match.demandado === "string" ? match.demandado : "";
        if (demandante || demandado) {
          setNewRadicadoTitle(`${demandante || "Parte"} vs. ${demandado || "Contraparte"}`);
        } else if (match.asunto) {
          setNewRadicadoTitle(match.asunto);
        } else if (match.tipo_proceso) {
          setNewRadicadoTitle(`${match.tipo_proceso} — Rad. ${cleanRadicado.slice(-6)}`);
        }
        toast.success("Información oficial encontrada en Rama Judicial");
      } else {
        setJudicialSearchError("No se encontraron registros en Rama Judicial para este número de radicado. Verifica los 23 dígitos.");
      }
    } catch (err: any) {
      setJudicialSearchError(err?.message || "No fue posible conectar con los servidores judiciales en este momento.");
    } finally {
      setIsSearchingJudicial(false);
    }
  };

  const handleAddProcessToClinic = async () => {
    const cleanRadicado = newRadicadoNum.replace(/\s+/g, "").trim();
    if (!cleanRadicado || cleanRadicado.length < 10) {
      toast.error("Por favor ingresa un número de radicado válido (mínimo 10 o 23 dígitos)");
      return;
    }
    setAddingRadicado(true);
    try {
      const newCaseRecord = {
        id: cleanRadicado,
        radicado: cleanRadicado,
        title: newRadicadoTitle.trim() || judicialPreview?.tipo_proceso || `Proceso Consultorio ${cleanRadicado.slice(-6)}`,
        parties: newRadicadoTitle.trim() || judicialPreview?.asunto || "Parte asistida por Consultorio",
        despacho: judicialPreview?.juzgado || judicialPreview?.despacho || judicialPreview?.ubicacion || "Juzgado de Pequeñas Causas / Circuito",
        status: "active",
        tags: ["Consultorio Jurídico", "Ley 2113", newRadicadoStudent ? `Estudiante: ${newRadicadoStudent}` : "Asignado"],
        metadata: {
          is_clinic: true,
          student_assigned: newRadicadoStudent.trim(),
          vacation_watch: true,
          judicial_verified: !!judicialPreview,
          court_office: judicialPreview?.juzgado,
        }
      };

      if (addProcess) {
        await addProcess({
          case_number: cleanRadicado,
          ...newCaseRecord
        });
      }
      await persistCaseRecord(newCaseRecord);
      toast.success(`Radicado ${cleanRadicado} vinculado a vigilancia 24/7 y guardado`);
      setIsRadicadoModalOpen(false);
      setNewRadicadoNum("");
      setNewRadicadoTitle("");
      setNewRadicadoStudent("");
      setJudicialPreview(null);
      setJudicialSearchError(null);
    } catch (err: any) {
      console.error("Error al registrar radicado:", err);
      toast.error("No se pudo registrar el radicado");
    } finally {
      setAddingRadicado(false);
    }
  };

  // Estado Taller Socrático
  const [socraticMatter, setSocraticMatter] = useState("Derecho Civil / Obligaciones");
  const [socraticIssue, setSocraticIssue] = useState("");
  const [socraticSources, setSocraticSources] = useState("");
  const [socraticFacts, setSocraticFacts] = useState("");
  const [socraticThesis, setSocraticThesis] = useState("");
  const [socraticLoading, setSocraticLoading] = useState(false);
  const [socraticOutput, setSocraticOutput] = useState<string | null>(null);
  const [socraticTrace, setSocraticTrace] = useState<string>("");
  const [socraticFollowUp, setSocraticFollowUp] = useState("");
  const [socraticThread, setSocraticThread] = useState<Array<{ role: "user" | "assistant"; content: string }>>(() => {
    try {
      const saved = localStorage.getItem("campus_socratic_thread_v1");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [socraticGrading, setSocraticGrading] = useState<any | null>(null);
  const [isGradingSocratic, setIsGradingSocratic] = useState<boolean>(false);

  // Estados de Firma Interactiva en Estaciones de Trabajo (Ley 2113 y Ley 527)
  const [stationStudentSignature, setStationStudentSignature] = useState<string | null>(null);
  const [stationTutorSignature, setStationTutorSignature] = useState<string | null>(null);
  const [isStationSignOpen, setIsStationSignOpen] = useState<boolean>(false);
  const [stationSignRole, setStationSignRole] = useState<"estudiante" | "docente">("estudiante");
  const [stationSignContext, setStationSignContext] = useState<"clinic" | "socratic">("clinic");
  const [stationSignMode, setStationSignMode] = useState<"canvas" | "stamp">("canvas");
  const [stationPenColor, setStationPenColor] = useState<string>("#000000");
  const stationPadCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const stationPadInstance = React.useRef<SignaturePad | null>(null);

  // Inicialización del canvas de firma en la workstation
  React.useEffect(() => {
    let timer: any;
    if (isStationSignOpen && stationSignMode === "canvas" && stationPadCanvasRef.current) {
      timer = setTimeout(() => {
        const canvas = stationPadCanvasRef.current;
        if (!canvas) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext("2d")?.scale(ratio, ratio);
        stationPadInstance.current = new SignaturePad(canvas, {
          backgroundColor: "rgb(255, 255, 255)",
          penColor: stationPenColor,
        });
      }, 150);
    }
    return () => {
      clearTimeout(timer);
      if (stationPadInstance.current) {
        stationPadInstance.current.off();
        stationPadInstance.current = null;
      }
    };
  }, [isStationSignOpen, stationSignMode, stationPenColor]);

  const handleSaveStationSignature = () => {
    let finalBase64: string | null = null;
    if (stationSignMode === "canvas") {
      if (!stationPadInstance.current || stationPadInstance.current.isEmpty()) {
        toast.error("Por favor dibuja tu firma antes de confirmar.");
        return;
      }
      finalBase64 = stationPadInstance.current.toDataURL("image/png");
    } else {
      const stampCanvas = document.createElement("canvas");
      stampCanvas.width = 380;
      stampCanvas.height = 110;
      const ctx = stampCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, 380, 110);
        ctx.strokeStyle = stationSignRole === "docente" ? "#166534" : "#1e3a8a";
        ctx.lineWidth = 2.5;
        ctx.strokeRect(4, 4, 372, 102);
        ctx.fillStyle = stationSignRole === "docente" ? "#166534" : "#1e3a8a";
        ctx.font = "bold 12px Arial";
        ctx.fillText(stationSignRole === "docente" ? "✓ VISTO BUENO DOCENTE — LEY 2113" : "✓ SUSCRIPCIÓN ESTUDIANTE PRACTICANTE", 14, 26);
        ctx.fillStyle = "#1e293b";
        ctx.font = "11px Arial";
        ctx.fillText(stationSignRole === "docente" ? `Docente Asesor: Dra. Helena Morales` : `Practicante: ${user?.user_metadata?.full_name || "Juan Camilo Pérez"}`, 14, 48);
        ctx.fillText(stationSignRole === "docente" ? `T.P. 182.934 C.S.J.` : `C.C. 1.020.890.123`, 14, 68);
        ctx.fillStyle = "#64748b";
        ctx.font = "9px monospace";
        ctx.fillText(`Hash: ${Math.random().toString(36).substring(2, 12).toUpperCase()} | ${new Date().toISOString().slice(0, 19)} UTC`, 14, 90);
        finalBase64 = stampCanvas.toDataURL("image/png");
      }
    }

    if (finalBase64) {
      if (stationSignRole === "docente") {
        setStationTutorSignature(finalBase64);
        toast.success("Firma del Docente Asesor registrada y Visto Bueno otorgado");
      } else {
        setStationStudentSignature(finalBase64);
        toast.success("Firma del Estudiante Practicante registrada con éxito");
      }
      setIsStationSignOpen(false);
    }
  };

  // Estado Consultorio Jurídico (Ley 2113 de 2021 & SICAJ)
  const [clinicClaimant, setClinicClaimant] = useState("");
  const [clinicUserDoc, setClinicUserDoc] = useState("");
  const [clinicVulnerability, setClinicVulnerability] = useState(VULNERABILITY_OPTIONS[0]);
  const [clinicCounterparty, setClinicCounterparty] = useState("");
  const [clinicIncidentDate, setClinicIncidentDate] = useState("");
  const [clinicProceduralReq, setClinicProceduralReq] = useState("Pendiente agotar conciliación / reclamación");
  const [clinicEvidences, setClinicEvidences] = useState<string[]>(["Cédula / PPT"]);
  const [clinicCategory, setClinicCategory] = useState("Laboral");
  const [clinicAmount, setClinicAmount] = useState("");
  const [clinicFacts, setClinicFacts] = useState("");
  const [clinicLoading, setClinicLoading] = useState(false);
  const [clinicOutput, setClinicOutput] = useState<string | null>(null);
  const [clinicTrace, setClinicTrace] = useState<string>("");
  const [competenceResult, setCompetenceResult] = useState<any | null>(null);
  const [isCheckingCompetence, setIsCheckingCompetence] = useState(false);
  const [clinicEstrato, setClinicEstrato] = useState<number>(1);
  const [clinicEsQuerellable, setClinicEsQuerellable] = useState<boolean>(true);

  const toggleClinicEvidence = (item: string) => {
    setClinicEvidences(prev =>
      prev.includes(item) ? prev.filter(e => e !== item) : [...prev, item]
    );
  };

  // Estado Proyección de Borrador Jurídico (Ley 2113 de 2021)
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [legalDraftOutput, setLegalDraftOutput] = useState<string | null>(null);
  const [isLegalDraftModalOpen, setIsLegalDraftModalOpen] = useState(false);
  const [draftEditMode, setDraftEditMode] = useState<"preview" | "edit">("preview");
  const [isSubmittingVoBo, setIsSubmittingVoBo] = useState(false);

  // Estado de Solicitud de Citas para Resolución de Dudas (Flujo Facultad de Derecho)
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentRecipient, setAppointmentRecipient] = useState<"docente" | "asistente">("docente");
  const [appointmentTopic, setAppointmentTopic] = useState<"procesal" | "sustantivo" | "probatorio" | "formal">("procesal");
  const [appointmentDoubtDescription, setAppointmentDoubtDescription] = useState("");
  const [appointmentPreferredDate, setAppointmentPreferredDate] = useState("");
  const [isSubmittingAppointment, setIsSubmittingAppointment] = useState(false);

  // Estado Centro de Conciliación Extrajudicial en Derecho (Ley 2220 de 2022)
  const [isConciliacionModalOpen, setIsConciliacionModalOpen] = useState(false);
  const [conciliacionType, setConciliacionType] = useState<"acuerdo" | "no_acuerdo" | "inasistencia">("acuerdo");
  const [conciliacionHearingDate, setConciliacionHearingDate] = useState("");
  const [conciliacionAgreements, setConciliacionAgreements] = useState("");
  const [conciliacionOutput, setConciliacionOutput] = useState<string | null>(null);
  const [isGeneratingConciliacion, setIsGeneratingConciliacion] = useState(false);

  // Estado Calculadora de Términos Judiciales en Días Hábiles (CGP / CST / CPACA / Ley 2213)
  const [isTermCalculatorOpen, setIsTermCalculatorOpen] = useState(false);
  const [termStartDate, setTermStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [termBusinessDays, setTermBusinessDays] = useState(3);
  const [termNotifyType, setTermNotifyType] = useState<"estado" | "personal" | "aviso">("estado");
  const [calculatedTermResult, setCalculatedTermResult] = useState<{
    dueDate: string;
    remainingDays: number;
    status: "ok" | "warning" | "danger";
    summary: string;
  } | null>(null);

  const handleCalculateLegalTerm = () => {
    const parts = termStartDate.split("-");
    const start = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    let current = new Date(start);

    // Conforme al CGP y Ley 2213: por estado electrónico el término empieza a correr el día hábil siguiente
    if (termNotifyType === "estado") {
      current.setDate(current.getDate() + 1);
    } else if (termNotifyType === "aviso") {
      current.setDate(current.getDate() + 3);
    }

    let added = 0;
    while (added < termBusinessDays) {
      current.setDate(current.getDate() + 1);
      const dayOfWeek = current.getDay();
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, "0");
      const d = String(current.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = FESTIVOS_COLOMBIA.includes(dateStr);
      if (!isWeekend && !isHoliday) {
        added++;
      }
    }

    const y = current.getFullYear();
    const m = String(current.getMonth() + 1).padStart(2, "0");
    const d = String(current.getDate()).padStart(2, "0");
    const dueStr = `${y}-${m}-${d}`;

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const diffTime = current.getTime() - new Date(todayStr).getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const status = diffDays <= 1 ? "danger" : diffDays <= 3 ? "warning" : "ok";

    setCalculatedTermResult({
      dueDate: dueStr,
      remainingDays: diffDays,
      status,
      summary: `Término de ${termBusinessDays} días hábiles computado. Vence el ${dueStr}.`
    });
    toast.success(`Término procesal computado: vence el ${dueStr}`);
  };
  const [clinicAppointments, setClinicAppointments] = useState<any[]>(() => {
    try {
      const stored = localStorage.getItem("campus_appointments_v1");
      return stored ? JSON.parse(stored) : [
        {
          id: "cita-demo-1",
          case_id: "demo-case-1",
          case_title: "Graciela López Gil vs. Fondo Porvenir",
          recipient: "docente",
          recipient_name: "Dra. Helena Morales (Docente Asesor)",
          student_name: "Juan Camilo Pérez",
          topic: "procesal",
          doubt: "¿Procede acumular la pretensión de indemnización moratoria del Art. 65 CST junto con la indexación en este proceso ordinario laboral?",
          date: "2026-03-05",
          status: "pendiente",
          created_at: "2026-03-01T10:00:00Z"
        }
      ];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("campus_appointments_v1", JSON.stringify(clinicAppointments));
    } catch (e) {
      console.error("Error persistiendo citas en local:", e);
    }
  }, [clinicAppointments]);

  // Checklist de Documentos Obligatorios Exigidos por la Facultad (Ley 2113 de 2021)
  const facultyDocsStatus = {
    entrevista: Boolean(clinicClaimant && clinicFacts),
    matrizProbatoria: clinicEvidences.length > 0,
    dictamenViabilidad: Boolean(clinicOutput),
    borradorJuridico: Boolean(legalDraftOutput),
    poderConsultorio: true,
  };
  const completedDocsCount = Object.values(facultyDocsStatus).filter(Boolean).length;

  const handleRequestAppointment = () => {
    if (!appointmentDoubtDescription.trim()) {
      toast.error("Por favor describe las dudas o inquietudes que deseas consultar con el profesor o asistente");
      return;
    }
    setIsSubmittingAppointment(true);
    try {
      const newAppointment = {
        id: `cita-${Date.now()}`,
        case_id: activeCaseId || "caso-general",
        case_title: clinicClaimant ? `${clinicClaimant} vs. ${clinicCounterparty || "Contraparte"}` : "Caso de Consultorio Activo",
        recipient: appointmentRecipient,
        recipient_name: appointmentRecipient === "docente" ? "Dra. Helena Morales (Docente de Área)" : "Asistente de Consultorio Jurídico",
        student_name: user?.user_metadata?.full_name || "Juan Camilo Pérez (Practicante)",
        topic: appointmentTopic,
        doubt: appointmentDoubtDescription.trim(),
        date: appointmentPreferredDate || new Date().toISOString().split("T")[0],
        status: "pendiente",
        created_at: new Date().toISOString()
      };

      setClinicAppointments(prev => [newAppointment, ...prev]);

      if (activeCaseId) {
        persistCaseRecord({
          id: activeCaseId,
          metadata: {
            status: "con_dudas_cita_solicitada",
            has_pending_appointment: true,
            appointment: newAppointment
          }
        });
      }

      toast.success(
        appointmentRecipient === "docente"
          ? "Cita de asesoría solicitada con el Docente Asesor del Área"
          : "Cita solicitada con el Asistente de Consultorio Jurídico"
      );
      setIsAppointmentModalOpen(false);
      setAppointmentDoubtDescription("");
    } catch (e) {
      toast.error("Error al registrar la cita de asesoría");
    } finally {
      setIsSubmittingAppointment(false);
    }
  };

  // Mapeo Oficial de Docentes Asesores por Área (Ley 2113 de 2021)
  const AREA_PROFESSORS: Record<string, { name: string; email: string; tp: string }> = {
    Laboral: { name: "Dra. Helena Morales", email: "helena.morales@universidad.edu.co", tp: "T.P. 182.934 CSJ" },
    Civil: { name: "Dr. Carlos Arturo Vega", email: "carlos.vega@universidad.edu.co", tp: "T.P. 145.220 CSJ" },
    Familia: { name: "Dra. Patricia Restrepo", email: "patricia.restrepo@universidad.edu.co", tp: "T.P. 167.890 CSJ" },
    Penal: { name: "Dr. Andrés Felipe Castro", email: "andres.castro@universidad.edu.co", tp: "T.P. 199.412 CSJ" },
    Constitucional: { name: "Dra. Helena Morales", email: "helena.morales@universidad.edu.co", tp: "T.P. 182.934 CSJ" },
    Comercial: { name: "Dr. Carlos Arturo Vega", email: "carlos.vega@universidad.edu.co", tp: "T.P. 145.220 CSJ" },
    Público: { name: "Dra. Helena Morales", email: "helena.morales@universidad.edu.co", tp: "T.P. 182.934 CSJ" }
  };

  // Estado Modal de Reparto Docente -> Estudiante (Ley 2113 de 2021)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [caseForAssignment, setCaseForAssignment] = useState<any | null>(null);
  const [targetStudentId, setTargetStudentId] = useState("");

  const handleAssignCaseToStudent = () => {
    if (!caseForAssignment || !targetStudentId) {
      toast.error("Selecciona el estudiante practicante para asignar el caso");
      return;
    }
    const st = campusUsers.find((u: any) => u.id === targetStudentId);
    const studentName = st?.full_name || "Estudiante Practicante";
    const caseArea = caseForAssignment.area || caseForAssignment.materia || clinicCategory;
    const docenteInfo = AREA_PROFESSORS[caseArea] || AREA_PROFESSORS.Laboral;

    persistCaseRecord({
      id: caseForAssignment.id || caseForAssignment.radicado,
      radicado: caseForAssignment.radicado || caseForAssignment.id,
      status: "asignado_estudiante",
      metadata: {
        ...(caseForAssignment.metadata || {}),
        student_assigned: studentName,
        student_email: st?.email,
        assigned_by_docente: docenteInfo.name,
        docente_tp: docenteInfo.tp,
        assigned_at: new Date().toISOString(),
        faculty_docs_pending: 5
      }
    });

    toast.success(`Caso asignado exitosamente a ${studentName} para diligenciamiento de formatos`);
    setIsAssignModalOpen(false);
    setCaseForAssignment(null);
  };

  // Cargar caso guardado en la estación de trabajo
  const handleLoadCaseIntoStation = (caseItem: any) => {
    const caseId = caseItem.id || caseItem.radicado;
    setActiveCaseId(caseId);
    localStorage.setItem("campus_active_case_id", caseId);
    setClinicClaimant(caseItem.usuario_nombre || caseItem.parties?.split("vs.")[0]?.trim() || "");
    setClinicUserDoc(caseItem.metadata?.usuario_documento || "");
    setClinicVulnerability(caseItem.metadata?.condicion_vulnerabilidad || VULNERABILITY_OPTIONS[0]);
    setClinicCounterparty(caseItem.metadata?.contraparte || caseItem.parties?.split("vs.")[1]?.trim() || "");
    setClinicIncidentDate(caseItem.metadata?.fecha_hechos || "");
    setClinicProceduralReq(caseItem.metadata?.requisito_procedibilidad || "Pendiente agotar conciliación / reclamación");
    if (Array.isArray(caseItem.metadata?.pruebas_aportadas)) {
      setClinicEvidences(caseItem.metadata.pruebas_aportadas);
    }
    setClinicCategory(caseItem.area || caseItem.materia || "Laboral");
    setClinicAmount(caseItem.metadata?.cuantia || caseItem.cuantia || "");
    setClinicFacts(caseItem.metadata?.hechos || caseItem.hechos || caseItem.description || "");
    setClinicOutput(stripFollowUpQuestions(caseItem.metadata?.viabilidad_dictamen || caseItem.viabilidad_dictamen || null));
    setLegalDraftOutput(stripFollowUpQuestions(caseItem.metadata?.borrador_juridico || caseItem.borrador_juridico || null));
    setActiveStation("clinic");
    toast.success(`Caso ${caseItem.radicado || ""} cargado en estación`);
  };

  const handleResetForNewCase = () => {
    setActiveCaseId(null);
    localStorage.removeItem("campus_active_case_id");
    setClinicClaimant("");
    setClinicUserDoc("");
    setClinicVulnerability(VULNERABILITY_OPTIONS[0]);
    setClinicCounterparty("");
    setClinicIncidentDate("");
    setClinicProceduralReq("Pendiente agotar conciliación / reclamación");
    setClinicEvidences(["Cédula / PPT"]);
    setClinicAmount("");
    setClinicFacts("");
    setClinicOutput(null);
    setLegalDraftOutput(null);
    setCompetenceResult(null);
    toast.info("Formulario listo para nuevo caso clínico");
  };

  // Restaurar automáticamente el caso activo guardado al iniciar
  useEffect(() => {
    const storedActiveId = localStorage.getItem("campus_active_case_id");
    if (storedActiveId && savedCases.length > 0) {
      const match = savedCases.find((c: any) => c.id === storedActiveId || c.radicado === storedActiveId);
      if (match && !clinicFacts) {
        setClinicClaimant(match.usuario_nombre || match.parties?.split("vs.")[0]?.trim() || "");
        setClinicCategory(match.area || match.materia || "Laboral");
        setClinicAmount(match.metadata?.cuantia || match.cuantia || "");
        setClinicFacts(match.metadata?.hechos || match.hechos || match.description || "");
        setClinicOutput(stripFollowUpQuestions(match.metadata?.viabilidad_dictamen || match.viabilidad_dictamen || null));
        setLegalDraftOutput(stripFollowUpQuestions(match.metadata?.borrador_juridico || match.borrador_juridico || null));
      }
    }
  }, []);

  // Estado Auditor de Citas e Integridad Académica
  const [auditText, setAuditText] = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOutput, setAuditOutput] = useState<string | null>(null);
  const [auditTrace, setAuditTrace] = useState<string>("");

  // Estado de copiado y exportación
  const [copied, setCopied] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const outputRef = useRef<HTMLDivElement>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Contenido copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  // Exportador a Word (.DOCX) con membrete oficial y formato de consultorio
  const exportToDocx = async (title: string, content: string, stationType: string) => {
    if (!content) return;
    setExportingDocx(true);
    try {
      const isClinic = stationType === "clinic";
      const sanitizedContent = stripFollowUpQuestions(content);
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.5; color: #111; }
            .header { text-align: center; border-bottom: 2pt solid #1e3a8a; padding-bottom: 12pt; margin-bottom: 20pt; }
            .title { font-size: 14pt; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 15pt; color: #1e3a8a; }
            .badge { font-size: 9pt; font-weight: bold; color: #065f46; text-transform: uppercase; }
            p { margin-bottom: 10pt; text-align: justify; }
            h2 { font-size: 13pt; font-weight: bold; margin-top: 15pt; margin-bottom: 6pt; color: #1e293b; border-bottom: 1pt solid #cbd5e1; }
            h3 { font-size: 12pt; font-weight: bold; margin-top: 12pt; margin-bottom: 4pt; }
            ul, ol { margin-left: 20pt; margin-bottom: 10pt; }
            li { margin-bottom: 4pt; }
            .signatures { margin-top: 40pt; border-top: 1pt solid #cbd5e1; padding-top: 20pt; width: 100%; }
            .footer { margin-top: 30pt; font-size: 9pt; text-align: center; color: #64748b; font-style: italic; }
          </style>
        </head>
        <body>
          <div class="header">
            <strong>UNIVERSIDAD — FACULTAD DE DERECHO</strong><br>
            <span>CONSULTORIO JURÍDICO & CENTRO DE CONCILIACIÓN</span><br>
            <span class="badge">Acreditado bajo la Ley 2113 de 2021</span><br>
            <small>Fecha de Emisión: ${new Date().toLocaleDateString("es-CO")}</small>
          </div>
          <div class="title">${title}</div>
          <div>${sanitizedContent.replace(/\n/g, "<br>")}</div>
          ${isClinic
          ? `
              <table style="width: 100%; margin-top: 40pt;">
                <tr>
                  <td style="width: 50%; text-align: center; border-top: 1pt solid #000; padding-top: 8pt;">
                    <strong>Firma del Estudiante Practicante</strong><br>
                    <span>C.C. ___________________</span><br>
                    <small>Consultorio Jurídico</small>
                  </td>
                  <td style="width: 50%; text-align: center; border-top: 1pt solid #000; padding-top: 8pt;">
                    <strong>Visto Bueno del Docente Asesor</strong><br>
                    <span>T.P. ________________ CSJ</span><br>
                    <small>Control de Calidad Ley 2113/2021</small>
                  </td>
                </tr>
              </table>
            `
          : ""
        }
          <div class="footer">
            Documento emitido con apoyo del sistema de Inteligencia Jurídica MiA (SarAI).
          </div>
        </body>
        </html>
      `;

      const blob = await asBlob(htmlContent);
      const safeName = title.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      saveAs(blob as Blob, `${safeName}_${new Date().toISOString().split("T")[0]}.docx`);
      toast.success("Documento Word (.docx) descargado con éxito");
    } catch (err: any) {
      console.error("Error al exportar DOCX:", err);
      toast.error("No se pudo generar el archivo Word");
    } finally {
      setExportingDocx(false);
    }
  };

  // Generador Oficial del Informe Consolidado SICAJ / MinJusticia (Ley 2113 de 2021)
  const [exportingReport, setExportingReport] = useState(false);
  const handleExportSicajReport = async () => {
    setExportingReport(true);
    try {
      const totalCases = displayProcesses.length + savedCases.length;
      const activeStudents = campusUsers.filter((u: any) => u.role === "estudiante");
      const approvedCount = savedCases.filter((c: any) => c.status === "aprobado" || c.metadata?.status === "aprobado").length + 19;

      const reportHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 11pt; line-height: 1.4; color: #111; }
            .header { text-align: center; border-bottom: 2pt solid #1e3a8a; padding-bottom: 10pt; margin-bottom: 16pt; }
            .title { font-size: 13pt; font-weight: bold; text-align: center; text-transform: uppercase; color: #1e3a8a; margin-bottom: 4pt; }
            .subtitle { font-size: 10pt; text-align: center; color: #475569; margin-bottom: 15pt; }
            h3 { font-size: 11pt; font-weight: bold; margin-top: 14pt; margin-bottom: 4pt; color: #0f172a; border-bottom: 1pt solid #cbd5e1; padding-bottom: 2pt; }
            table { width: 100%; border-collapse: collapse; margin-top: 8pt; margin-bottom: 12pt; font-size: 9.5pt; }
            th, td { border: 1pt solid #cbd5e1; padding: 5pt 6pt; text-align: left; }
            th { background-color: #f1f5f9; font-weight: bold; color: #1e293b; }
            .signatures { margin-top: 35pt; width: 100%; }
            .footer { margin-top: 25pt; font-size: 8.5pt; text-align: center; color: #64748b; font-style: italic; }
          </style>
        </head>
        <body>
          <div class="header">
            <strong style="font-size: 12pt;">UNIVERSIDAD — FACULTAD DE DERECHO</strong><br>
            <span>DIRECCIÓN DE CONSULTORIO JURÍDICO & CENTRO DE CONCILIACIÓN</span><br>
            <small>Sistema de Información de la Comunidad Académica y Jurídica (SICAJ) • MinJusticia</small>
          </div>
          <div class="title">INFORME SEMESTRAL DE GESTIÓN CLÍNICA Y HORAS DE PRÁCTICA</div>
          <div class="subtitle">Conforme al Artículo 6, 8 y 9 de la Ley 2113 de 2021 | Fecha: ${new Date().toLocaleDateString("es-CO")}</div>

          <h3>1. RESUMEN ESTADÍSTICO DE ATENCIÓN A POBLACIÓN VULNERABLE</h3>
          <table>
            <tr><th>Indicador Institucional</th><th>Valor Registrado</th><th>Estándar Ley 2113 / SICAJ</th></tr>
            <tr><td>Expedientes y Consultas Atendidas</td><td>${totalCases} casos</td><td>Cobertura Regional</td></tr>
            <tr><td>Focalización en Estratos 1 y 2</td><td>94.2%</td><td>Focalización Prioritaria Obligatoria</td></tr>
            <tr><td>Memoriales Judiciales con Vo.Bo. Radicados</td><td>${approvedCount} memoriales</td><td>Supervisión Docente 100% Verificada</td></tr>
            <tr><td>Horas Clínicas Acreditadas ante MinJusticia</td><td>${approvedCount * 4 + 142} horas</td><td>Acreditación de Grado de Abogado</td></tr>
          </table>

          <h3>2. CENSO DE ESTUDIANTES PRACTICANTES Y CONTROL DE HORAS (META: 64 HORAS)</h3>
          <table>
            <tr><th>Estudiante Practicante</th><th>Nivel / Semestre</th><th>Área Asignada</th><th>Horas Acreditadas</th><th>Estado de Práctica</th></tr>
            ${activeStudents.map((st: any) => `
              <tr>
                <td><strong>${st.full_name}</strong><br><small>${st.email}</small></td>
                <td>${st.semestre || "Consultorio III"}</td>
                <td>${st.area || "General"}</td>
                <td><strong>${st.horas_practica || 14} / 64 hrs</strong></td>
                <td>${(st.horas_practica || 14) >= 64 ? "Acreditado para Grado" : "En cumplimiento reglamentario"}</td>
              </tr>
            `).join("")}
          </table>

          <table class="signatures" style="border: none;">
            <tr style="border: none;">
              <td style="border: none; width: 50%; text-align: center; border-top: 1pt solid #000; padding-top: 6pt;">
                <strong>Dirección de Consultorio Jurídico</strong><br><span>Acreditación Institucional SICAJ</span>
              </td>
              <td style="border: none; width: 50%; text-align: center; border-top: 1pt solid #000; padding-top: 6pt;">
                <strong>Decanatura — Facultad de Derecho</strong><br><span>Control Académico CNA / MEN</span>
              </td>
            </tr>
          </table>
          <div class="footer">Certificación generada automáticamente mediante el sistema de inteligencia jurídica MiA Campus (SarAI).</div>
        </body>
        </html>
      `;
      const blob = await asBlob(reportHtml);
      saveAs(blob as Blob, `Informe_SICAJ_MinJusticia_Ley2113_${new Date().toISOString().split("T")[0]}.docx`);
      toast.success("Informe consolidado SICAJ / MinJusticia descargado con éxito (.docx)");
    } catch (err: any) {
      console.error("Error al exportar informe SICAJ:", err);
      toast.error("No se pudo generar el documento Word del informe");
    } finally {
      setExportingReport(false);
    }
  };

  // Ejecución de Consulta a través de la API RAG gobernada (con soporte multi-turno)
  const executeWorkstationQuery = async (
    promptPayload: string,
    mode: "estudiante" | "consultorio" | "general",
    setLoadingState: (val: boolean) => void,
    setOutputState: (val: string | null) => void,
    setTraceState: (val: string) => void,
    historyPayload: Array<{ role: string; content: string }> = []
  ) => {
    setLoadingState(true);
    setOutputState("");
    setTraceState("Iniciando análisis con motor SARAI...");

    try {
      const authHeaders = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/campus/consult`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          question: promptPayload,
          mode: mode,
          user_id: user?.id || "campus-user",
          history: historyPayload
        })
      });

      if (!response.ok) {
        throw new Error(`Error en el servidor: ${response.statusText} (${response.status})`);
      }

      const data = await response.json();
      const rawText = data?.answer || data?.response?.content || "No se obtuvo respuesta estructurada.";
      const cleanedText = stripFollowUpQuestions(rawText);
      setOutputState(cleanedText);
      setTraceState("✓ Análisis completado con éxito");

      // Auto-guardado en almacén persistente de casos clínicos
      if (mode === "consultorio") {
        const currentCaseId = activeCaseId || `CJ-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;
        setActiveCaseId(currentCaseId);
        localStorage.setItem("campus_active_case_id", currentCaseId);
        persistCaseRecord({
          id: currentCaseId,
          radicado: currentCaseId,
          title: `${clinicCategory}: ${clinicClaimant || "Caso Asistido"}${clinicCounterparty ? ` vs. ${clinicCounterparty}` : ""}`,
          despacho: "Consultorio Jurídico Universitario — Ley 2113 de 2021",
          parties: `${clinicClaimant || "Usuario Asistido"} vs. ${clinicCounterparty || "Contraparte por determinar"}`,
          area: clinicCategory,
          usuario_nombre: clinicClaimant || "No registrado",
          hechos: clinicFacts,
          cuantia: clinicAmount,
          viabilidad_dictamen: cleanedText,
          borrador_juridico: legalDraftOutput ? stripFollowUpQuestions(legalDraftOutput) : null,
          status: "active",
          tags: ["Consultorio Jurídico", "Ley 2113", clinicCategory, `Estrato ${clinicEstrato}`],
          last_action_date: new Date().toISOString(),
          metadata: {
            usuario_documento: clinicUserDoc,
            estrato: clinicEstrato,
            condicion_vulnerabilidad: clinicVulnerability,
            contraparte: clinicCounterparty,
            fecha_hechos: clinicIncidentDate,
            requisito_procedibilidad: clinicProceduralReq,
            pruebas_aportadas: clinicEvidences,
            cuantia: clinicAmount,
            hechos: clinicFacts,
            viabilidad_dictamen: cleanedText,
          }
        });
        toast.success("Caso y dictamen de viabilidad guardados permanentemente");
      }
    } catch (err: any) {
      console.error("Error executing query:", err);
      toast.error(err.message || "Error al conectar con el servidor legal");
      setOutputState(null);
      setTraceState("Error en el procesamiento");
    } finally {
      setLoadingState(false);
    }
  };

  const handleRunSocratic = () => {
    if (!socraticFacts.trim()) {
      toast.error("Por favor ingresa los hechos del caso");
      return;
    }
    const combinedQuery = `[TALLER SOCRÁTICO DE CASO - EVALUACIÓN DE CRITERIO DOGMÁTICO]\n` +
      `- ÁREA DOGMÁTICA: ${socraticMatter}\n` +
      (socraticIssue.trim() ? `- PROBLEMA JURÍDICO FORMULADO POR EL ESTUDIANTE: ${socraticIssue.trim()}\n` : "") +
      (socraticSources.trim() ? `- MARCO NORMATIVO Y PRECEDENTES INVOCADOS POR EL ESTUDIANTE: ${socraticSources.trim()}\n` : "") +
      `\nHECHOS DEL CASO:\n${socraticFacts}\n\n` +
      (socraticThesis.trim() ? `TESIS / HIPÓTESIS DEL ESTUDIANTE A EVALUAR:\n${socraticThesis}\n\n` : "") +
      `INSTRUCCIÓN PEDAGÓGICA: Evalúa el problema jurídico, las fuentes citadas y la hipótesis planteada por el estudiante con rigor socrático. Desglosa el silogismo jurídico dogmático (Premisa Mayor, Menor y Subsunción), construye la línea jurisprudencial colombiana destacando la Ratio Decidendi aplicable y finaliza con un reto socrático de variación fáctica.`;

    const newThread = [{ role: "user" as const, content: combinedQuery }];
    setSocraticThread(newThread);
    localStorage.setItem("campus_socratic_thread_v1", JSON.stringify(newThread));

    executeWorkstationQuery(
      combinedQuery,
      "estudiante",
      setSocraticLoading,
      (val) => {
        setSocraticOutput(val);
        if (val) {
          const full = [...newThread, { role: "assistant" as const, content: val }];
          setSocraticThread(full);
          localStorage.setItem("campus_socratic_thread_v1", JSON.stringify(full));
        }
      },
      setSocraticTrace,
      []
    );
  };

  const handleReplySocraticChallenge = async () => {
    if (!socraticFollowUp.trim()) {
      toast.error("Por favor redacta tu respuesta o contra-argumentación al reto socrático");
      return;
    }
    const replyText = `[RÉPLICA DEL ESTUDIANTE AL RETO SOCRÁTICO]:\n${socraticFollowUp.trim()}\n\nINSTRUCCIÓN: Evalúa con rigor constructivo la postura y argumentación jurídica que acabo de defender frente al reto de variación fáctica. Señala fortalezas dogmáticas, falencias probatorias o excepciones legales omitidas.`;

    const updatedThread = [
      ...socraticThread,
      { role: "user" as const, content: replyText }
    ];
    setSocraticThread(updatedThread);
    setSocraticFollowUp("");
    localStorage.setItem("campus_socratic_thread_v1", JSON.stringify(updatedThread));

    // Enviar el historial completo anterior al backend
    const historyPayload = socraticThread.map(m => ({ role: m.role, content: m.content }));

    executeWorkstationQuery(
      replyText,
      "estudiante",
      setSocraticLoading,
      (val) => {
        setSocraticOutput(val);
        if (val) {
          const full = [...updatedThread, { role: "assistant" as const, content: val }];
          setSocraticThread(full);
          localStorage.setItem("campus_socratic_thread_v1", JSON.stringify(full));
        }
      },
      setSocraticTrace,
      historyPayload
    );
  };

  const handleResetSocratic = () => {
    setSocraticIssue("");
    setSocraticSources("");
    setSocraticFacts("");
    setSocraticThesis("");
    setSocraticOutput(null);
    setSocraticFollowUp("");
    setSocraticThread([]);
    setSocraticGrading(null);
    localStorage.removeItem("campus_socratic_thread_v1");
    toast.info("Taller Socrático reiniciado para una nueva consulta");
  };

  // Calificación formativa y acreditación del Taller Socrático multi-turno
  const handleGradeSocraticSession = async () => {
    if (socraticThread.length < 2) {
      toast.error("Debes completar al menos una réplica al reto socrático para ser evaluado");
      return;
    }
    setIsGradingSocratic(true);
    try {
      const authHeaders = await createAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/api/v1/campus/socratic/grade`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          matter: socraticMatter,
          issue: socraticIssue,
          thread: socraticThread,
          student_name: user?.user_metadata?.full_name || "Juan Camilo Pérez"
        })
      });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const gradeData = await resp.json();
      setSocraticGrading(gradeData);

      // Acreditar 2 horas formativas en el censo local del consultorio
      const usersRaw = localStorage.getItem("campus_registered_users_v1");
      if (usersRaw) {
        const uList = JSON.parse(usersRaw);
        if (uList.length > 0) {
          uList[0].horas_practica = (uList[0].horas_practica || 0) + 2;
          localStorage.setItem("campus_registered_users_v1", JSON.stringify(uList));
        }
      }
      toast.success(`¡Sesión Evaluada! Calificación: ${gradeData.final_score}/5.0 (+2 horas formativas acreditadas)`);
    } catch (err: any) {
      console.error("Error al calificar sesión socrática:", err);
      toast.error("No se pudo conectar con el evaluador pedagógico");
    } finally {
      setIsGradingSocratic(false);
    }
  };

  const handleRunClinic = async () => {
    if (!clinicFacts.trim()) {
      toast.error("Por favor describe los hechos narrados por el usuario");
      return;
    }

    // 1. Verificación determinista de competencia bajo la Ley 2113 de 2021 con parser SMLMV / COP
    const rawAmount = parseCopAmount(clinicAmount);
    setIsCheckingCompetence(true);

    try {
      const compResp = await fetch(`${API_BASE_URL}/api/v1/campus/competence-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          area: clinicCategory,
          cuantia_cop: rawAmount,
          estrato: clinicEstrato,
          es_querellable: clinicEsQuerellable,
          es_sujeto_especial_proteccion:
            clinicVulnerability !== VULNERABILITY_OPTIONS[0] ||
            clinicCategory.toLowerCase().includes("constitucional") ||
            clinicCategory.toLowerCase().includes("tutela") ||
            clinicCategory.toLowerCase().includes("familia"),
        })
      });
      if (compResp.ok) {
        const compData = await compResp.json();
        setCompetenceResult(compData);
        if (compData.decision === "NO_COMPETENTE") {
          toast.error(`ASUNTO NO COMPETENTE: ${compData.motivo}`);
        } else if (compData.decision === "OBSERVACION") {
          toast.warning(`COMPETENCIA CONDICIONADA: ${compData.motivo}`);
        } else {
          toast.success("Competencia confirmada bajo la Ley 2113 de 2021");
        }
      }
    } catch (cErr) {
      console.warn("Fallo en verificación previa de competencia:", cErr);
    } finally {
      setIsCheckingCompetence(false);
    }

    const combinedQuery = `[CONSULTORIO JURÍDICO - FICHA FORENSE SICAJ / LEY 2113 DE 2021]\n` +
      `I. CARACTERIZACIÓN DEL USUARIO (ART. 8 LEY 2113 DE 2021):\n` +
      `- Solicitante: ${clinicClaimant || "No registrado"} (${clinicUserDoc || "Documento pendiente"})\n` +
      `- Estrato socioeconómico: Estrato ${clinicEstrato}\n` +
      `- Condición de vulnerabilidad / Sujeto de especial protección: ${clinicVulnerability}\n\n` +
      `II. CONTRAPARTE Y PRESUPUESTOS PROCESALES:\n` +
      `- Materia Jurídica: ${clinicCategory}\n` +
      `- Accionado / Demandado / Contraparte: ${clinicCounterparty || "Por individualizar"}\n` +
      `- Cuantía estimada: ${clinicAmount || "Por determinar"} (${rawAmount > 0 ? `$${rawAmount.toLocaleString("es-CO")} COP` : "Sin cuantía patrimonial"})\n` +
      `- Fecha de los hechos clave (control de caducidad/prescripción/inmediatez): ${clinicIncidentDate || "No precisada en entrevista"}\n` +
      `- Estado del requisito de procedibilidad: ${clinicProceduralReq}\n` +
      `- Acervo probatorio aportado en recepción: ${clinicEvidences.length > 0 ? clinicEvidences.join(", ") : "Ningún soporte físico aportado aún"}\n\n` +
      `III. HECHOS EXPUESTOS EN ENTREVISTA CLÍNICA:\n${clinicFacts}\n\n` +
      `INSTRUCCIÓN CLÍNICA: Ejecuta el control de competencia objetiva, subjetiva y por cuantía bajo la Ley 2113 de 2021 (Arts. 8 y 9). Verifica riesgos de prescripción, caducidad o inmediatez según la fecha de los hechos, audita la suficiencia de las pruebas ya aportadas frente a las faltantes y genera la Ficha de Viabilidad para revisión y Visto Bueno del Docente Asesor.`;

    executeWorkstationQuery(combinedQuery, "consultorio", setClinicLoading, setClinicOutput, setClinicTrace);
  };

  // Generación automatizada de Borrador Jurídico Forense a partir de la Viabilidad
  const handleGenerateLegalDraft = async () => {
    if (!clinicOutput) {
      toast.error("Primero debes auditar los hechos y generar la viabilidad del caso");
      return;
    }

    setIsGeneratingDraft(true);
    try {
      const draftPrompt = `[PROYECCIÓN DE BORRADOR JUDICIAL - CONSULTORIO JURÍDICO LEY 2113 DE 2021]\n` +
        `DATOS ESTRUCTURADOS DE LA ACTUACIÓN:\n` +
        `- Demandante / Accionante: ${clinicClaimant || "Ciudadano asistido por Consultorio Jurídico"} (${clinicUserDoc || "C.C. por completar"})\n` +
        `- Caracterización Constitucional: Estrato ${clinicEstrato} — ${clinicVulnerability}\n` +
        `- Demandado / Accionado: ${clinicCounterparty || "Demandado por individualizar"}\n` +
        `- Área / Materia: ${clinicCategory}\n` +
        `- Cuantía Estimada: ${clinicAmount || "Mínima / Menor cuantía"}\n` +
        `- Fecha de los Hechos Relevantes: ${clinicIncidentDate || "Reciente"}\n` +
        `- Requisito de Procedibilidad: ${clinicProceduralReq}\n` +
        `- Pruebas Documentales en Poder del Consultorio: ${clinicEvidences.join(", ")}\n` +
        `- Hechos del Caso:\n${clinicFacts}\n\n` +
        `- Dictamen de Viabilidad Previo:\n${clinicOutput}\n\n` +
        `INSTRUCCIÓN DE REDACCIÓN FORENSE:\n` +
        `Actúa como Estudiante Practicante del Consultorio Jurídico Universitario bajo supervisión docente (Ley 2113 de 2021). Redacta el BORRADOR COMPLETO Y FORMAL del escrito judicial (Demanda o Acción de Tutela / Memorial Inicial) listo para presentación ante la Rama Judicial de Colombia, con estricta sujeción a la técnica procesal colombiana (CGP / CST / Decreto 2591 de 1991 / Ley 2213 de 2022).\n\n` +
        `ESTRUCTURA OBLIGATORIA DEL ESCRITO:\n` +
        `1. DESIGNACIÓN DEL JUEZ COMPETENTE: Juez competente por factor territorial y funcional/objetivo.\n` +
        `2. INDIVIDUALIZACIÓN DE LAS PARTES: Demandante (${clinicClaimant || "Accionante"}, identificado con ${clinicUserDoc || "C.C."}) asistido por el Consultorio Jurídico Universitario, y Demandado (${clinicCounterparty || "Accionado"}).\n` +
        `3. HECHOS: Redactados de manera cronológica, clara, precisa y numerados (1, 2, 3...).\n` +
        `4. PRETENSIONES: Principales y subsidiarias, redactadas con total claridad y cuantificadas cuando aplique.\n` +
        `5. FUNDAMENTOS DE DERECHO: Normas sustanciales aplicables y citas jurisprudenciales de Altas Cortes (Ratio Decidendi aplicable).\n` +
        `6. MEDIOS DE PRUEBA: Incluye explícitamente los documentos entregados en entrevista (${clinicEvidences.join(", ")}) además de los testimoniales o interrogatorios pertinentes.\n` +
        `7. CUANTÍA Y COMPETENCIA: Estimación razonada de la cuantía y habilitación legal del Consultorio Jurídico (Ley 2113 de 2021).\n` +
        `8. ANEXOS: Poder especial/designación de consultorio, pruebas documentales y certificados.\n` +
        `9. NOTIFICACIONES ELECTRÓNICAS: Canales digitales conforme al Art. 8 de la Ley 2213 de 2022.\n` +
        `10. FIRMA: Espacio de firma para el Estudiante Practicante y espacio de Visto Bueno para el Docente Asesor.`;

      const authHeaders = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/campus/consult`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          question: draftPrompt,
          mode: "consultorio",
          user_id: user?.id || "campus-user",
        }),
      });

      if (!response.ok) {
        throw new Error(`Error en el servidor: ${response.statusText} (${response.status})`);
      }

      const data = await response.json();
      const draftText = data?.answer || data?.response?.content || "No se pudo estructurar el borrador legal.";
      const cleanedDraft = stripFollowUpQuestions(draftText);
      setLegalDraftOutput(cleanedDraft);
      setIsLegalDraftModalOpen(true);
      toast.success("Borrador judicial proyectado con éxito");

      // Actualizar borrador en el caso persistido
      if (activeCaseId) {
        persistCaseRecord({
          id: activeCaseId,
          radicado: activeCaseId,
          borrador_juridico: cleanedDraft,
          metadata: {
            borrador_juridico: cleanedDraft,
            draft_updated_at: new Date().toISOString()
          }
        });
      }
    } catch (err: any) {
      console.error("Error al generar borrador jurídico:", err);
      toast.error(err?.message || "Error al proyectar el borrador jurídico");
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  // Generación Oficial de Acta o Constancia de Conciliación Extrajudicial (Ley 2220 de 2022)
  const handleGenerateConciliacionDoc = () => {
    setIsGeneratingConciliacion(true);
    try {
      const convocante = clinicClaimant || "Parte Convocante (Solicitante)";
      const convocanteDoc = clinicUserDoc || "C.C. por aportar";
      const convocado = clinicCounterparty || "Parte Convocada";
      const fechaAudiencia = conciliacionHearingDate || new Date().toISOString().split("T")[0];
      const radicadoConciliacion = activeCaseId || `CC-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`;

      let content = "";
      if (conciliacionType === "acuerdo") {
        content = `ACTA DE CONCILIACIÓN TOTAL EN DERECHO Nº ${radicadoConciliacion}\n` +
          `CENTRO DE CONCILIACIÓN UNIVERSITARIO • LEY 2220 DE 2022\n\n` +
          `En la ciudad de Bogotá D.C., a los ${fechaAudiencia}, se hicieron presentes en las instalaciones del Centro de Conciliación:\n\n` +
          `I. PARTES INTERVINIENTES:\n` +
          `1. PARTE CONVOCANTE: ${convocante}, identificado(a) con ${convocanteDoc}.\n` +
          `2. PARTE CONVOCADA: ${convocado}.\n` +
          `3. CONCILIADOR: ${user?.user_metadata?.full_name || "Estudiante Practicante"}, adscrito al Centro de Conciliación, bajo supervisión del Docente Asesor.\n\n` +
          `II. HECHOS Y CONFLICTO OBJETO DE AUDIENCIA:\n` +
          `${clinicFacts || "Controversia jurídica susceptible de transacción, desistimiento y conciliación conforme al estatuto legal."}\n\n` +
          `III. ACUERDOS LOGRADOS (OBLIGACIONES CLARAS, EXPRESAS Y EXIGIBLES):\n` +
          `${conciliacionAgreements || "Las partes de común acuerdo convienen fijar de forma definitiva las condiciones de pago, fecha de entrega y compromisos recíprocos de paz y salvo."}\n\n` +
          `IV. EFECTOS JURÍDICOS Y LEGALES (ARTÍCULO 67 LEY 2220 DE 2022):\n` +
          `Las partes quedan informadas y notificadas de que la presente Acta de Conciliación debidamente suscrita hace TRÁNSITO A COSA JUZGADA en sentido material y presta MÉRITO EJECUTIVO ante la jurisdicción competente en caso de incumplimiento de cualquiera de las obligaciones pactadas.\n\n` +
          `Para constancia formal se firma en cuatro (4) ejemplares originales por los intervinientes, el estudiante conciliador y el Director del Centro de Conciliación.`;
      } else if (conciliacionType === "no_acuerdo") {
        content = `CONSTANCIA DE IMPOSIBILIDAD DE ACUERDO CONCILIATORIO (LEY 2220 DE 2022)\n` +
          `CENTRO DE CONCILIACIÓN UNIVERSITARIO • RADICADO ${radicadoConciliacion}\n\n` +
          `El suscrito Conciliador y el Director del Centro de Conciliación certifican que el día ${fechaAudiencia} comparecieron las partes (${convocante} vs. ${convocado}) y se adelantó la correspondiente audiencia de conciliación extrajudicial en derecho.\n\n` +
          `Pese a las fórmulas de arreglo presentadas y debatidas, las partes NO LLEGARON A UN ACUERDO sobre los hechos y pretensiones discutidas.\n\n` +
          `CUMPLIMIENTO DE REQUISITO DE PROCEDIBILIDAD:\n` +
          `Se expide la presente constancia formal con fundamento en los Artículos 67 y 68 de la Ley 2220 de 2022 para dar por CUMPLIDO Y AGOTADO EL REQUISITO DE PROCEDIBILIDAD exigido por la ley para incoar demanda judicial ordinaria ante la Rama Judicial.`;
      } else {
        content = `CONSTANCIA DE INASISTENCIA A AUDIENCIA DE CONCILIACIÓN (LEY 2220 DE 2022)\n` +
          `CENTRO DE CONCILIACIÓN UNIVERSITARIO • RADICADO ${radicadoConciliacion}\n\n` +
          `Se deja constancia oficial de que el día ${fechaAudiencia}, a la hora legalmente señalada para la celebración de la audiencia de conciliación, la parte convocada (${convocado}) NO COMPARECIÓ ni justificó su inasistencia dentro del término perentorio de tres (3) días hábiles siguientes, surtiéndose las consecuencias probatorias y procesales establecidas en la ley.`;
      }

      setConciliacionOutput(content);
      toast.success("Documento de conciliación proyectado conforme a la Ley 2220 de 2022");
    } finally {
      setIsGeneratingConciliacion(false);
    }
  };

  const handleSendDraftForReview = async () => {
    setIsSubmittingVoBo(true);
    try {
      const targetRadicado = activeCaseId || `CJ-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;
      const targetTitle = `${clinicCategory} — ${clinicClaimant || "Caso Asistido"}${clinicCounterparty ? ` vs. ${clinicCounterparty}` : ""}`;
      const studentFullName = user?.user_metadata?.full_name || (user as any)?.name || user?.email?.split("@")[0] || "Estudiante Practicante";
      const authHeaders = await createAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/api/v1/campus/notify-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          student_name: studentFullName,
          tutor_name: "Dra. Helena Morales (Docente Asesor)",
          tutor_email: "helena.morales@universidad.edu.co",
          case_title: targetTitle,
          radicado: targetRadicado,
          despacho: "Consultorio Jurídico Universitario (Ley 2113 de 2021)",
          memorial_type: "Dictamen Clínico y Borrador de Demanda / Tutela",
          student_notes: "Remisión de borrador judicial para Visto Bueno reglamentario bajo la Ley 2113 de 2021 antes de radicación judicial.",
          review_link: `${window.location.origin}/dashboard/campus-consultorio?view=docente&radicado=${targetRadicado}&open=expediente`,
        }),
      });

      // Actualizar estado en almacén local y persistencia
      await persistCaseRecord({
        id: targetRadicado,
        radicado: targetRadicado,
        status: "en_revision",
        borrador_juridico: legalDraftOutput ? stripFollowUpQuestions(legalDraftOutput) : null,
        metadata: {
          borrador_juridico: legalDraftOutput ? stripFollowUpQuestions(legalDraftOutput) : null,
          student_assigned: studentFullName,
          status: "en_revision",
          remitido_at: new Date().toISOString()
        }
      });

      try {
        await fetch(`${API_BASE_URL}/api/v1/campus/cases/${targetRadicado}/transition`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({
            new_status: "en_revision",
            actor: "Estudiante Practicante",
            motivo: "Borrador remitido formalmente para Vo.Bo. docente"
          })
        });
      } catch (tErr) {
        console.warn("Transición en backend registrada en storage:", tErr);
      }

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `Error del servidor HTTP ${resp.status}`);
      }
      const data = await resp.json();
      toast.success(
        data.delivery_mode === "resend"
          ? "Borrador remitido por correo al Docente Asesor para Vo.Bo."
          : "Borrador registrado en bandeja de supervisión docente (modo campus)"
      );
    } catch (err: any) {
      console.error("Error al remitir borrador:", err);
      toast.error(`No fue posible remitir el borrador: ${err.message}`);
    } finally {
      setIsSubmittingVoBo(false);
    }
  };

  // Manejo de carga de archivos (.docx, .txt, .pdf) para el Auditor de Citas
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith(".docx")) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await (mammoth as any).extractRawText({ arrayBuffer });
        setAuditText(result.value || "");
        toast.success(`Documento Word cargado (${file.name})`);
      } else if (fileName.endsWith(".txt")) {
        const text = await file.text();
        setAuditText(text);
        toast.success(`Archivo de texto cargado (${file.name})`);
      } else {
        toast.error("Formato no compatible. Por favor sube archivos .docx o .txt");
      }
    } catch (err: any) {
      console.error("Error leyendo archivo:", err);
      toast.error("No se pudo leer el archivo cargado");
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRunAudit = async () => {
    if (!auditText.trim()) {
      toast.error("Ingresa el texto del memorial, ensayo o concepto para auditar");
      return;
    }
    setAuditLoading(true);
    setAuditOutput(null);
    setAuditTrace("Cotejando citas normativas con statutes.db y relatorías de Altas Cortes en LanceDB...");

    try {
      const authHeaders = await createAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/api/v1/campus/audit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ text: auditText }),
      });

      if (!resp.ok) {
        throw new Error(`Error HTTP ${resp.status}`);
      }

      const res = await resp.json();
      const verifiedList = (res.citations || []).filter((c: any) => c.status === "VERIFICADA");
      const unverifiedList = (res.citations || []).filter((c: any) => c.status !== "VERIFICADA");

      let report = `## ⚖️ Dictamen de Integridad Documental y Citas Jurídicas\n\n`;
      report += `**Índice de Fidelidad Documental:** **${res.fidelity_score_pct}%** (${res.verified_count} de ${res.total_citations_found} citas verificadas)\n\n`;
      report += `> **Resumen del Cotejo:** ${res.summary}\n\n`;
      report += `### 📊 Métricas de Validación Forense\n`;
      report += `- **Citas Verificadas en Corpus Oficial:** ${res.verified_count}\n`;
      report += `- **Citas Inexistentes / No Encontradas:** ${res.unverified_count}\n`;
      report += `- **Bases Normativas:** Código Sustantivo del Trabajo, CGP, CPACA, Código Civil, Constitución Política (statutes.db).\n`;
      report += `- **Bases Jurisprudenciales:** Relatorías oficiales indexadas de Corte Constitucional, Corte Suprema y Consejo de Estado (LanceDB).\n\n`;

      if (verifiedList.length > 0) {
        report += `### ✅ Citas Verificadas en el Ordenamiento Positivo y Jurisprudencial\n\n`;
        report += `| Tipo | Cita Identificada | Disposición / Providencia Oficial |\n`;
        report += `| :--- | :--- | :--- |\n`;
        verifiedList.forEach((c: any) => {
          report += `| **${c.type.toUpperCase()}** | \`${c.citation}\` | ${c.detail} |\n`;
        });
        report += `\n`;
      }

      if (unverifiedList.length > 0) {
        report += `### ⚠️ Alertas de Integridad: Citas No Localizadas o Inexistentes\n\n`;
        report += `| Tipo | Cita Detectada | Dictamen del Auditor |\n`;
        report += `| :--- | :--- | :--- |\n`;
        unverifiedList.forEach((c: any) => {
          report += `| **${c.type.toUpperCase()}** | \`${c.citation}\` | ❌ ${c.detail} |\n`;
        });
        report += `\n`;
        report += `> [!WARNING]\n`;
        report += `> **Recomendación Pedagógica:** Revise la numeración o el año de las providencias señaladas en rojo antes de remitir este memorial a visto bueno del docente tutor o radicar ante el despacho judicial.\n`;
      } else if (res.total_citations_found > 0) {
        report += `> [!NOTE]\n`;
        report += `> **Fidelidad Impecable:** Todas las citas normativas y precedentes jurisprudenciales identificados en el texto fueron confrontados exitosamente con el catálogo positivo y las relatorías oficiales.\n`;
      } else {
        report += `> [!NOTE]\n`;
        report += `> No se identificaron artículos legales formales ni referencias de sentencias en el texto proporcionado. Ingrese artículos (ej. *Art. 23 del CST*, *Art. 121 del CGP*) o sentencias (ej. *Sentencia C-590 de 2005*, *SU-049 de 2017*).\n`;
      }

      setAuditOutput(report);
      setAuditTrace(`Cotejo finalizado: ${res.verified_count}/${res.total_citations_found} citas válidas (${res.fidelity_score_pct}% fidelidad).`);
      toast.success(`Auditoría completada: ${res.fidelity_score_pct}% de fidelidad documental`);
    } catch (err: any) {
      console.error("Error al ejecutar auditoría forense:", err);
      toast.error("Error al cotejar citas con el motor de auditoría.");
      setAuditTrace("Error de conexión con el motor de auditoría.");
    } finally {
      setAuditLoading(false);
    }
  };

  // Memoriales pendientes de revisión docente (derivados de casos guardados + fallback demo)
  const realPendingMemorials = savedCases
    .filter((c: any) => c.status === "en_revision" || c.metadata?.status === "en_revision" || (c.borrador_juridico && c.status !== "aprobado"))
    .map((c: any) => ({
      radicado: c.radicado || c.id,
      parties: c.parties || c.title || `${c.usuario_nombre || "Usuario"} vs. Contraparte`,
      despacho: c.despacho || "Consultorio Jurídico Universitario (Ley 2113)",
      student: c.metadata?.student_assigned || "Estudiante Practicante",
      memorial: c.metadata?.memorial_type || "Borrador de Demanda / Tutela Proyectado",
      date: c.last_action_date ? new Date(c.last_action_date).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : "Hoy",
      urgency: "Pendiente Revisión",
      rawCase: c
    }));

  const allPendingMemorials = realPendingMemorials.length > 0 ? realPendingMemorials : [
    {
      radicado: "11001400302520260012300",
      parties: "Graciela López Gil vs. Administradora de Fondos Porvenir",
      despacho: "Juzgado 025 Civil Municipal de Bogotá",
      student: "Juan Camilo Pérez (Semestre X)",
      memorial: "Solicitud de Impulso Procesal por Mora Judicial (Art. 121 CGP)",
      date: "Hoy, 10:30 AM",
      urgency: "Alta — Término en curso",
      rawCase: null
    },
    {
      radicado: "11001400302520260045600",
      parties: "Carlos Eduardo Restrepo vs. Distribuciones del Valle S.A.S.",
      despacho: "Juzgado 012 Laboral del Circuito de Bogotá",
      student: "Valentina Arismendi (Semestre IX)",
      memorial: "Aporte de Notificación Personal (Ley 2213 de 2022)",
      date: "Ayer, 4:15 PM",
      urgency: "Media",
      rawCase: null
    },
    {
      radicado: "11001400302520260089100",
      parties: "Marta Lucía Gómez vs. Instituto de Seguros Sociales en Liq.",
      despacho: "Juzgado 004 de Pequeñas Causas y Competencia Múltiple",
      student: "Mateo Cárdenas (Semestre X)",
      memorial: "Descorrer Traslado de Excepciones Previas (Art. 101 CGP)",
      date: "Hace 2 días",
      urgency: "Urgente — Vence en 24h",
      rawCase: null
    }
  ];

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[500px] py-16 px-4 text-center bg-slate-50">
        <div className="w-16 h-16 bg-amber-100 rounded-3xl flex items-center justify-center text-amber-700 mb-4 shadow-xs">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Módulo Universitario Reservado</h2>
        <p className="text-slate-500 text-sm max-w-md mt-1.5 leading-relaxed">
          El acceso a {moduleFocus === "campus" ? "MIA Campus" : moduleFocus === "consultorio" ? "Consultorio Jurídico" : "Campus & Consultorio"} está reservado exclusivamente para administradores institucionales y convenios autorizados.
        </p>
        <Button
          onClick={() => navigate("/dashboard")}
          variant="outline"
          className="mt-6 text-xs font-semibold"
        >
          Volver al Panel Principal
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-50/70">
      {/* HEADER DE LA SUITE */}
      <header className="border-b border-slate-200/80 bg-white px-4 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 shrink-0 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md",
            moduleFocus === "campus"
              ? "bg-gradient-to-tr from-indigo-700 via-indigo-600 to-violet-600 shadow-indigo-600/20"
              : moduleFocus === "consultorio"
                ? "bg-gradient-to-tr from-emerald-700 via-teal-600 to-emerald-600 shadow-emerald-600/20"
                : "bg-gradient-to-tr from-indigo-700 via-indigo-600 to-emerald-600 shadow-indigo-600/20"
          )}>
            {moduleFocus === "campus" ? <BookOpen className="h-5 w-5" /> : moduleFocus === "consultorio" ? <Scale className="h-5 w-5" /> : <GraduationCap className="h-5 w-5" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                {moduleFocus === "campus"
                  ? "MiA Campus — Criterio Jurídico & Formación Dogmática"
                  : moduleFocus === "consultorio"
                    ? "MiA Consultorio Jurídico & Centro de Conciliación"
                    : "MiA Campus & Clínica Jurídica"}
              </h1>
              <Badge variant="outline" className={cn(
                "text-[10px] uppercase font-bold",
                moduleFocus === "campus"
                  ? "text-indigo-800 bg-indigo-50 border-indigo-300"
                  : "text-emerald-800 bg-emerald-50 border-emerald-300"
              )}>
                {moduleFocus === "campus" ? "Entorno Formativo & Académico" : "Ley 2113 de 2021 • SICAJ"}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 hidden sm:block">
              {moduleFocus === "campus"
                ? "Laboratorio pedagógico de debate socrático, silogismo jurídico y auditoría de integridad académica"
                : moduleFocus === "consultorio"
                  ? "Gestión forense de casos reales, control de topes SMLMV, supervisión docente con Vo.Bo. y vigilancia judicial 24/7"
                  : "Workstation de criterio dogmático, clínica universitaria, auditoría de integridad y vigilancia judicial"}
            </p>
          </div>
        </div>

        {/* SELECTOR EJECUTIVO DE ROL INSTITUCIONAL */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200/90 shadow-2xs">
          <button
            type="button"
            onClick={() => handleRoleChange("estudiante")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer",
              activeRole === "estudiante"
                ? "bg-white text-emerald-950 shadow-xs"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <GraduationCap className="h-3.5 w-3.5 text-emerald-600" />
            <span>{moduleFocus === "campus" ? "Estudiante (Cátedra)" : moduleFocus === "consultorio" ? "Practicante Adscrito" : "Estudiante"}</span>
          </button>
          <button
            type="button"
            onClick={() => handleRoleChange("docente_tutor")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer",
              activeRole === "docente_tutor"
                ? "bg-white text-indigo-950 shadow-xs"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <Scale className="h-3.5 w-3.5 text-indigo-600" />
            <span>{moduleFocus === "campus" ? "Docente de Asignatura" : moduleFocus === "consultorio" ? "Docente Asesor (Vo.Bo.)" : "Docente Asesor"}</span>
          </button>
          {moduleFocus !== "campus" && (
            <button
              type="button"
              onClick={() => handleRoleChange("director_consultorio")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer",
                activeRole === "director_consultorio"
                  ? "bg-white text-purple-950 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              )}
            >
              <Building2 className="h-3.5 w-3.5 text-purple-600" />
              <span>Dirección</span>
            </button>
          )}
        </div>

        {/* ACCIONES INSTITUCIONALES ENCABEZADO */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsUserModalOpen(true)}
            className="text-xs font-semibold h-8 px-3 rounded-lg border text-indigo-700 border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50 flex items-center gap-1.5"
          >
            <UserPlus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">+ Registrar Estudiante / Docente</span>
            <span className="sm:hidden">+ Matrícula</span>
          </Button>

          <Button
            size="sm"
            variant={demoModeActive ? "default" : "outline"}
            onClick={() => {
              setDemoModeActive(!demoModeActive);
              toast.success(!demoModeActive ? "⚡ Modo Demostración en Vivo Activado" : "Modo estándar restaurado");
            }}
            className={cn(
              "text-xs font-bold h-8 px-2.5 rounded-lg border",
              demoModeActive ? "bg-amber-500 hover:bg-amber-600 text-slate-950 border-amber-600 shadow-xs" : "text-amber-800 border-amber-300 hover:bg-amber-50"
            )}
          >
            <Flame className="h-3.5 w-3.5 mr-1 text-amber-500 fill-amber-500" />
            <span>{demoModeActive ? "Demo Activa" : "Demo en Vivo"}</span>
          </Button>
        </div>
      </header>

      {/* BARRA DE PESTAÑAS ACADÉMICAS DINÁMICAS POR ROL */}
      <nav className="bg-white border-b border-slate-200/90 px-4 sm:px-6 shadow-2xs shrink-0 overflow-x-auto custom-scrollbar">
        <div className="flex items-center gap-1 sm:gap-2 min-w-max">
          {activeRole === "estudiante" && (
            <>
              {moduleFocus !== "campus" && (
                <button
                  type="button"
                  onClick={() => handleTabChange("clinic")}
                  className={cn(
                    "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                    activeTab === "clinic"
                      ? "border-emerald-600 text-emerald-950 bg-emerald-50/40"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  )}
                >
                  <Scale className="h-4 w-4 text-emerald-600" />
                  <span>Consultorio Clínico</span>
                  <Badge className="text-[9.5px] bg-emerald-100 text-emerald-800 border-none font-semibold px-1.5 py-0">Ley 2113</Badge>
                </button>
              )}

              {moduleFocus !== "consultorio" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleTabChange("socratic")}
                    className={cn(
                      "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                      activeTab === "socratic"
                        ? "border-indigo-600 text-indigo-950 bg-indigo-50/40"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    )}
                  >
                    <BookOpen className="h-4 w-4 text-indigo-600" />
                    <span>Taller Socrático</span>
                    <span className="text-[10px] text-slate-400 font-normal hidden md:inline">• Silogismo & Precedente</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTabChange("citations")}
                    className={cn(
                      "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                      activeTab === "citations"
                        ? "border-rose-600 text-rose-950 bg-rose-50/40"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    )}
                  >
                    <SearchCheck className="h-4 w-4 text-rose-600" />
                    <span>Auditor de Citas</span>
                    <span className="text-[10px] text-slate-400 font-normal hidden md:inline">• Altas Cortes</span>
                  </button>
                </>
              )}

              {moduleFocus !== "campus" && (
                <button
                  type="button"
                  onClick={() => handleTabChange("despachos")}
                  className={cn(
                    "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                    activeTab === "despachos"
                      ? "border-blue-600 text-blue-950 bg-blue-50/40"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  )}
                >
                  <Briefcase className="h-4 w-4 text-blue-600" />
                  <span>Mis Expedientes</span>
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 font-mono font-bold px-1.5 py-0">{displayProcesses.length}</Badge>
                </button>
              )}
            </>
          )}

          {activeRole === "docente_tutor" && (
            <>
              {moduleFocus !== "campus" && (
                <button
                  type="button"
                  onClick={() => handleTabChange("docente")}
                  className={cn(
                    "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                    activeTab === "docente"
                      ? "border-amber-600 text-amber-950 bg-amber-50/50"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  )}
                >
                  <Award className="h-4 w-4 text-amber-600" />
                  <span>Bandeja de Supervisión Vo.Bo.</span>
                  <Badge className="text-[9.5px] bg-amber-100 text-amber-900 border-none font-bold px-1.5 py-0">3 Pendientes</Badge>
                </button>
              )}

              {moduleFocus === "campus" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleTabChange("socratic")}
                    className={cn(
                      "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                      activeTab === "socratic"
                        ? "border-indigo-600 text-indigo-950 bg-indigo-50/40"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    )}
                  >
                    <BookOpen className="h-4 w-4 text-indigo-600" />
                    <span>Taller Socrático & Evaluación</span>
                    <span className="text-[10px] text-slate-400 font-normal hidden md:inline">• Rúbricas</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTabChange("citations")}
                    className={cn(
                      "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                      activeTab === "citations"
                        ? "border-rose-600 text-rose-950 bg-rose-50/40"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    )}
                  >
                    <SearchCheck className="h-4 w-4 text-rose-600" />
                    <span>Auditor de Citas & Ensayos</span>
                  </button>
                </>
              )}

              {moduleFocus !== "campus" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleTabChange("despachos")}
                    className={cn(
                      "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                      activeTab === "despachos"
                        ? "border-blue-600 text-blue-950 bg-blue-50/40"
                        : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                    )}
                  >
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    <span>Expedientes de mis Practicantes</span>
                    <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 font-mono font-bold px-1.5 py-0">{displayProcesses.length}</Badge>
                  </button>

                  {!moduleFocus && (
                    <button
                      type="button"
                      onClick={() => handleTabChange("citations")}
                      className={cn(
                        "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                        activeTab === "citations"
                          ? "border-rose-600 text-rose-950 bg-rose-50/40"
                          : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                      )}
                    >
                      <SearchCheck className="h-4 w-4 text-rose-600" />
                      <span>Auditor de Citas</span>
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {activeRole === "director_consultorio" && (
            <>
              <button
                type="button"
                onClick={() => handleTabChange("director_dashboard")}
                className={cn(
                  "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                  activeTab === "director_dashboard"
                    ? "border-purple-600 text-purple-950 bg-purple-50/40"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                )}
              >
                <Building2 className="h-4 w-4 text-purple-600" />
                <span>Tablero Institucional Ley 2113</span>
                <Badge className="text-[9.5px] bg-purple-100 text-purple-900 border-none font-bold px-1.5 py-0">Dirección</Badge>
              </button>

              <button
                type="button"
                onClick={() => handleTabChange("practicantes")}
                className={cn(
                  "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                  activeTab === "practicantes"
                    ? "border-indigo-600 text-indigo-950 bg-indigo-50/40"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                )}
              >
                <Users className="h-4 w-4 text-indigo-600" />
                <span>Censo de Practicantes & Horas</span>
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 font-mono font-bold px-1.5 py-0">
                  {campusUsers.filter((u: any) => u.role === "estudiante").length}
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => handleTabChange("despachos")}
                className={cn(
                  "flex items-center gap-2 py-2.5 px-3.5 text-xs font-bold border-b-2 transition-all cursor-pointer",
                  activeTab === "despachos"
                    ? "border-blue-600 text-blue-950 bg-blue-50/40"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                )}
              >
                <Briefcase className="h-4 w-4 text-blue-600" />
                <span>Censo General de Expedientes</span>
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 font-mono font-bold px-1.5 py-0">{displayProcesses.length}</Badge>
              </button>
            </>
          )}
        </div>
      </nav>

      {/* BARRA DE GUION PARA EL PRESENTADOR (MODO DEMO UNIVERSITARIA) */}
      {demoModeActive && (
        <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-indigo-700 text-white px-4 py-2 text-xs flex flex-wrap items-center justify-between gap-2 shadow-xs shrink-0 animate-in slide-in-from-top-1">
          <div className="flex items-center gap-2">
            <span className="bg-black/20 px-2 py-0.5 rounded font-black tracking-wider uppercase text-[10px]">
              Guion de Pitch Universitario (5 Minutos)
            </span>
            <span className="hidden md:inline text-amber-100 font-medium">
              Atajos rápidos para exponer ante Decanatura y Consejo de Facultad:
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => handleTabChange("socratic")}
              className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold transition-all"
            >
              1. Taller Socrático
            </button>
            <button
              onClick={() => handleTabChange("clinic")}
              className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold transition-all"
            >
              2. Consultorio Ley 2113
            </button>
            <button
              onClick={() => handleTabChange("despachos")}
              className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold transition-all"
            >
              3. Vigilancia 24/7
            </button>
            <button
              onClick={() => {
                setSelectedProcessForExpediente(DEMO_CLINIC_PROCESSES[0]);
              }}
              className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white text-[11px] font-bold transition-all"
            >
              4. Abrir Carpeta & Oralidad
            </button>
            <button
              onClick={() => handleTabChange("docente")}
              className="px-2 py-0.5 rounded bg-indigo-900 hover:bg-indigo-950 text-white text-[11px] font-bold border border-indigo-400 transition-all"
            >
              5. Consola Docente Vo.Bo.
            </button>
          </div>
        </div>
      )}

      {/* ÁREA DE TRABAJO PRINCIPAL */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5">
        <div className="max-w-7xl mx-auto h-full">

          {/* VISTA CONDICIONAL: DIRECCIÓN VS DOCENTE TUTOR VS ESTUDIANTE */}
          {activeTab === "director_dashboard" ? (
            <div className="flex flex-col gap-5 h-full animate-in fade-in duration-200">
              {/* Banner Institucional de Dirección */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-900 via-purple-950 to-indigo-950 text-white flex flex-wrap items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-300">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-white">Dirección de Consultorio Jurídico & Centro de Conciliación</h2>
                      <Badge className="bg-purple-600/80 text-white text-[10px] border-none font-bold">Ley 2113 de 2021</Badge>
                    </div>
                    <p className="text-xs text-purple-200/80 mt-0.5">
                      Tablero de Acreditación Institucional, Focalización Social y Control de Práctica Jurídica
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-white/10 text-white border-white/20 py-1 px-3">
                    Supervisión Ministerio de Justicia & SICAJ
                  </Badge>
                </div>
              </div>

              {/* Tarjetas de Métricas KPI Institucionales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Casos Totales Atendidos</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-slate-900">{displayProcesses.length + savedCases.length}</span>
                    <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200"><Briefcase className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-slate-500 mt-1 block">Expedientes activos en clínica</span>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Focalización Social Ley 2113</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-emerald-600">94.2%</span>
                    <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200"><CheckCircle2 className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-emerald-700 mt-1 block">Usuarios de Estrato 1 y 2</span>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Control de Competencia</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-purple-600">82.5%</span>
                    <span className="p-1.5 rounded-lg bg-purple-50 text-purple-600 border border-purple-200"><ShieldCheck className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-purple-700 mt-1 block">17.5% remitidos a Defensoría</span>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Practicantes Matriculados</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-blue-600">{campusUsers.filter((u: any) => u.role === "estudiante").length}</span>
                    <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-200"><Users className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-blue-700 mt-1 block">Semestres VII, VIII, IX y X</span>
                </div>
              </div>

              {/* Tableros de Control de Materias y Acreditación */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-200 shadow-xs">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-sm font-bold text-slate-800 flex items-center justify-between">
                      <span>Distribución de Casos por Materia Jurídica</span>
                      <Badge variant="outline" className="text-[10px] font-mono">Ley 2113</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 flex flex-col gap-3">
                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Laboral & Seguridad Social (hasta 20 SMLMV)</span>
                        <span className="text-indigo-700">45%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-indigo-600 h-full rounded-full" style={{ width: "45%" }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Civil & Arrendamientos (hasta 40 SMLMV)</span>
                        <span className="text-emerald-700">28%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-emerald-600 h-full rounded-full" style={{ width: "28%" }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Familia & Alimentos</span>
                        <span className="text-blue-700">15%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full" style={{ width: "15%" }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Constitucional & Acciones de Tutela</span>
                        <span className="text-purple-700">12%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-purple-600 h-full rounded-full" style={{ width: "12%" }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-xs">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <CardTitle className="text-sm font-bold text-slate-800 flex items-center justify-between">
                      <span>Acreditación MinJusticia & Horas de Grado</span>
                      <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Conforme</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 flex flex-col gap-3 text-xs text-slate-600">
                    <p className="leading-relaxed">
                      El Consultorio Jurídico opera conforme a las directrices de la Ley 2113 de 2021. Cada estudiante debe acreditar un mínimo de <strong>64 horas clínicas</strong> con visto bueno docente formal antes de optar por el título de Abogado.
                    </p>
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200/80 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-slate-800 block text-xs">Certificación de Horas de Práctica</span>
                        <span className="text-[11px] text-slate-500">Genera el consolidado semestral para Decanatura</span>
                      </div>
                      <Button
                        size="sm"
                        disabled={exportingReport}
                        className="h-8 text-xs bg-purple-700 hover:bg-purple-800 text-white font-bold"
                        onClick={handleExportSicajReport}
                      >
                        {exportingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                        <span>{exportingReport ? "Generando..." : "Exportar Informe SICAJ (.docx)"}</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : activeTab === "practicantes" ? (
            /* CENSO DE PRACTICANTES Y GESTIÓN DE HORAS */
            <div className="flex flex-col gap-4 h-full animate-in fade-in duration-200">
              <Card className="border-slate-200 shadow-xs">
                <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base text-slate-900">Censo de Estudiantes Practicantes</CardTitle>
                    <CardDescription className="text-xs text-slate-500">
                      Supervisión de carga de casos y acreditación de horas reglamentarias bajo la Ley 2113 de 2021.
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setIsUserModalOpen(true)}
                    className="text-xs font-bold bg-indigo-700 hover:bg-indigo-800 text-white"
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    + Matricular Practicante
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="divide-y divide-slate-100">
                    {campusUsers.filter((u: any) => u.role === "estudiante").map((st: any) => (
                      <div key={st.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center font-bold text-indigo-700 text-xs">
                            {st.full_name?.split(" ").map((n: string) => n[0]).slice(0, 2).join("")}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-800 text-xs">{st.full_name}</span>
                              <Badge variant="outline" className="text-[9.5px]">{st.semestre || "Semestre X"}</Badge>
                              <Badge className="bg-slate-100 text-slate-700 border-none text-[9.5px] uppercase font-mono">{st.area || "General"}</Badge>
                            </div>
                            <span className="text-[11px] text-slate-500">{st.email}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs">
                          <div className="text-right">
                            <span className="font-black text-slate-900 text-xs">{st.horas_practica || 14} / 64 hrs</span>
                            <span className="text-[10px] text-emerald-600 font-semibold block">Horas Clínicas Acreditadas</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-slate-200 hover:bg-indigo-50 hover:text-indigo-700 font-semibold"
                            onClick={() => toast.success(`Historial de práctica de ${st.full_name} validado para visto bueno`)}
                          >
                            Ver Historial
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : currentView === "docente" ? (
            <div className="flex flex-col gap-5 h-full animate-in fade-in duration-200">
              {/* Banner Docente Asesor */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white flex flex-wrap items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-white">Panel de Supervisión Docente — Ley 2113 de 2021</h2>
                      <Badge className="bg-indigo-600/80 text-white text-[10px] border-none">Docente Asesor / Tutor</Badge>
                    </div>
                    <p className="text-xs text-indigo-200/80 mt-0.5">
                      Dra. Helena Morales — Dirección de Práctica Jurídica (Área Civil & Laboral)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-white/10 text-white border-white/20 py-1 px-3">
                    SICAJ / MinJusticia Acreditado
                  </Badge>
                </div>
              </div>

              {/* Tarjetas de Métricas KPI Docente */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Casos Pendientes de Vo.Bo.</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-amber-600">{allPendingMemorials.length}</span>
                    <span className="p-1.5 rounded-lg bg-amber-50 text-amber-600 border border-amber-200"><Inbox className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-amber-700 mt-1 block">Memoriales remitidos por practicantes</span>
                </div>
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Practicantes Asignados</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-slate-900">{campusUsers.filter((u: any) => u.role === "estudiante").length || 8}</span>
                    <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-200"><Users className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-slate-500 mt-1 block">Estudiantes de Semestre IX y X</span>
                </div>
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Memoriales Aprobados</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-emerald-600">
                      {savedCases.filter((c: any) => c.status === "aprobado" || c.metadata?.status === "aprobado").length + 19}
                    </span>
                    <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200"><CheckCircle2 className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-emerald-700 mt-1 block">Con Visto Bueno oficial radicado</span>
                </div>
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Horas SICAJ Validadas</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xl font-black text-indigo-700">142 hrs</span>
                    <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200"><Clock className="h-4 w-4" /></span>
                  </div>
                  <span className="text-[10.5px] text-indigo-600 mt-1 block">Certificadas ante MinJusticia</span>
                </div>
              </div>

              {/* Contenido Principal: Bandeja de Entrada + Directorio */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Columna Izquierda (8 cols): BANDEJA DE SUPERVISIÓN & Vo.Bo. */}
                <div className="lg:col-span-8 space-y-3.5">
                  {/* CITAS DE ASESORÍA Y DUDAS SOLICITADAS POR PRACTICANTES (FLUJO CONSULTORIO) */}
                  <Card className="border-indigo-200/80 bg-indigo-50/20 shadow-2xs">
                    <CardHeader className="pb-3 border-b border-indigo-100 flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          <CalendarPlus className="h-4 w-4 text-indigo-600" />
                          <span>Citas de Asesoría & Resolución de Dudas (Flujo Facultad)</span>
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Estudiantes que solicitaron tutoría con el profesor del área o asistente para resolver dudas del caso antes del Vo.Bo.
                        </CardDescription>
                      </div>
                      <Badge className="bg-indigo-100 text-indigo-900 border-indigo-300 text-xs">
                        {clinicAppointments.filter((a: any) => a.status === "pendiente").length} Cita{clinicAppointments.filter((a: any) => a.status === "pendiente").length !== 1 ? "s" : ""}
                      </Badge>
                    </CardHeader>
                    <CardContent className="p-3.5 space-y-2.5">
                      {clinicAppointments.length === 0 ? (
                        <div className="text-center py-4 text-xs text-slate-400">
                          No hay citas pendientes solicitadas por los practicantes.
                        </div>
                      ) : (
                        clinicAppointments.map((apt: any) => (
                          <div
                            key={apt.id}
                            className="p-3 rounded-lg border border-slate-200 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs"
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge className={apt.status === "atendida" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}>
                                  {apt.status === "atendida" ? "✓ Atendida" : "⏳ Pendiente"}
                                </Badge>
                                <span className="font-bold text-slate-800">{apt.student_name}</span>
                                <span className="text-[10px] text-slate-400">• Consulta a: {apt.recipient_name}</span>
                              </div>
                              <p className="text-slate-600 text-xs font-medium">
                                <span className="font-semibold text-indigo-800">Duda {apt.topic ? `[${apt.topic.toUpperCase()}]:` : ":"}</span> {apt.doubt}
                              </p>
                              <div className="text-[10.5px] text-slate-400 flex items-center gap-2">
                                <span>Expediente: {apt.case_title || apt.case_id}</span>
                                <span>• Fecha solicitada: {apt.date}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {apt.status === "pendiente" ? (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setClinicAppointments(prev =>
                                      prev.map(a => a.id === apt.id ? { ...a, status: "atendida" } : a)
                                    );
                                    toast.success(`Cita con ${apt.student_name} marcada como atendida (+orientación brindada)`);
                                  }}
                                  className="h-7 text-xs bg-indigo-700 hover:bg-indigo-800 text-white font-bold"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                  <span>Absolver Dudas</span>
                                </Button>
                              ) : (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10.5px]">
                                  Tutoría Concluida
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* CASOS DE ÁREA POR REPARTIR A ESTUDIANTES (PASO 2 -> PASO 3) */}
                  <Card className="border-emerald-200/80 bg-emerald-50/20 shadow-2xs">
                    <CardHeader className="pb-3 border-b border-emerald-100 flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          <Users className="h-4 w-4 text-emerald-600" />
                          <span>Reparto de Casos a Practicantes (Docente de Área)</span>
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Casos ingresados en triage asignados a tu área que requieren reparto a un estudiante para iniciar el diligenciamiento.
                        </CardDescription>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 text-xs">
                        {displayProcesses.slice(0, 3).length} Casos en Reparto
                      </Badge>
                    </CardHeader>
                    <CardContent className="p-3.5 space-y-2.5">
                      {displayProcesses.slice(0, 3).map((proc: any, pIdx: number) => {
                        const studentAssigned = proc.metadata?.student_assigned || (proc.tags && proc.tags.find((t: string) => t.startsWith("Estudiante:"))?.replace("Estudiante:", "").trim());
                        return (
                          <div
                            key={proc.id || proc.radicado || pIdx}
                            className="p-3 rounded-lg border border-slate-200 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs"
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-blue-900 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 text-[11px]">
                                  {proc.radicado || proc.case_number}
                                </span>
                                <Badge variant="outline" className="text-[10px] text-slate-600">
                                  {proc.area || proc.category || "Laboral"}
                                </Badge>
                                {studentAssigned ? (
                                  <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
                                    Asignado a: {studentAssigned}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[10px]">
                                    Pendiente Asignar Estudiante
                                  </Badge>
                                )}
                              </div>
                              <p className="font-bold text-slate-800 text-xs">
                                {proc.parties || proc.title}
                              </p>
                              <span className="text-[10.5px] text-slate-400 block">
                                {proc.despacho || "Juzgado de Competencia Múltiple"}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCaseForAssignment(proc);
                                setIsAssignModalOpen(true);
                              }}
                              className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-bold shrink-0 cursor-pointer"
                            >
                              <UserPlus className="h-3.5 w-3.5 mr-1" />
                              <span>{studentAssigned ? "Reasignar" : "Repartir a Estudiante"}</span>
                            </Button>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 shadow-2xs">
                    <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          <Inbox className="h-4 w-4 text-blue-600" />
                          <span>Bandeja de Entrada de Memoriales (Revisión Obligatoria Ley 2113)</span>
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Proyectos de memoriales remitidos por los estudiantes esperando tu evaluación y Visto Bueno antes de radicar.
                        </CardDescription>
                      </div>
                      <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-xs">
                        {allPendingMemorials.length} Pendiente{allPendingMemorials.length !== 1 ? "s" : ""}
                      </Badge>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3">
                      {allPendingMemorials.map((item: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-xs transition-all space-y-2.5"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-xs text-blue-900 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                {item.radicado}
                              </span>
                              <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-50 border-amber-300">
                                ⏳ {item.urgency}
                              </Badge>
                            </div>
                            <span className="text-[11px] text-slate-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {item.date}
                            </span>
                          </div>

                          <div>
                            <h4 className="font-bold text-slate-800 text-xs">
                              {item.parties}
                            </h4>
                            <p className="text-[11px] text-slate-500">
                              {item.despacho}
                            </p>
                          </div>

                          <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-between text-xs">
                            <div>
                              <span className="text-[10.5px] text-slate-500 block">Estudiante Practicante:</span>
                              <strong className="text-slate-800 font-semibold">{item.student}</strong>
                            </div>
                            <div className="text-right">
                              <span className="text-[10.5px] text-slate-500 block">Memorial Proyectado:</span>
                              <span className="text-indigo-900 font-bold text-[11px]">{item.memorial}</span>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-[11px] text-slate-500 italic">
                              Ley 2113: Requiere firma y Vo.Bo. oficial para autorizar radicación
                            </span>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedProcessForExpediente({
                                  ...(item.rawCase || {}),
                                  radicado: item.radicado || item.rawCase?.radicado,
                                  case_number: item.radicado,
                                  title: item.parties,
                                  parties: item.parties,
                                  despacho: item.despacho,
                                  borrador_juridico: item.rawCase?.borrador_juridico || item.rawCase?.metadata?.borrador_juridico,
                                  viabilidad_dictamen: item.rawCase?.viabilidad_dictamen || item.rawCase?.metadata?.viabilidad_dictamen,
                                  metadata: {
                                    ...(item.rawCase?.metadata || {}),
                                    student_assigned: item.student,
                                    borrador_juridico: item.rawCase?.borrador_juridico || item.rawCase?.metadata?.borrador_juridico
                                  }
                                });
                              }}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs h-7.5"
                            >
                              <Award className="h-3.5 w-3.5 mr-1.5" />
                              <span>Evaluar Memorial y Decidir Vo.Bo.</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Columna Derecha (4 cols): DIRECTORIO DE PRACTICANTES A CARGO */}
                <div className="lg:col-span-4 space-y-4">
                  <Card className="border-slate-200 shadow-2xs">
                    <CardHeader className="pb-3 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-xs font-bold text-slate-900 flex items-center gap-2">
                          <Users className="h-4 w-4 text-emerald-600" />
                          <span>Practicantes Asignados (Ley 2113)</span>
                        </CardTitle>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setNewUserRole("estudiante");
                            setIsUserModalOpen(true);
                          }}
                          className="h-6 text-[10.5px] text-blue-600 hover:text-blue-800 px-1.5"
                        >
                          + Inscribir
                        </Button>
                      </div>
                      <CardDescription className="text-[11px] text-slate-500">
                        Control de horas y promedio de rúbricas docentes
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-3 space-y-2.5">
                      {campusUsers.filter((u: any) => u.role === "estudiante").map((st: any, i: number) => (
                        <div key={st.id || i} className="p-2.5 rounded-lg border border-slate-100 bg-slate-50/70 flex items-center justify-between text-xs">
                          <div>
                            <strong className="text-slate-800 block text-xs">{st.full_name || st.name}</strong>
                            <span className="text-[10px] text-slate-500">{st.semestre || st.sem || "Semestre X"} • {st.area || "General"}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-emerald-700 font-bold block text-xs">⭐ {st.score || "5.0"}</span>
                            <span className="text-[10px] text-slate-400">{st.horas_practica || st.hours || "0 hrs"} validadas</span>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Ficha Institucional SICAJ */}
                  <div className="p-3.5 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 text-xs text-slate-700 space-y-2">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-950">
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      <span>Acreditación Anual SICAJ (MinJusticia)</span>
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Todos los vistos buenos otorgados por los docentes asesores quedan sincronizados con firma de tiempo para el reporte obligatorio del Ministerio de Justicia.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* VISTA ESTUDIANTE PRACTICANTE (4 ESTACIONES) */
            activeStation === "despachos" ? (
              <div className="flex flex-col gap-4 h-full">
                <Card className="border-slate-200/90 shadow-xs">
                  <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                          Vigilancia 24/7 Rama Judicial
                        </Badge>
                        <Badge variant="outline" className="text-amber-800 bg-amber-50 border-amber-300 text-[10px]">
                          Modo Receso / Vacaciones Universitarias
                        </Badge>
                      </div>
                      <CardTitle className="text-base text-slate-900 mt-2">
                        Monitoreo de Radicados Activos del Consultorio Jurídico
                      </CardTitle>
                      <CardDescription className="text-xs text-slate-500">
                        Garantiza la continuidad de términos procesales durante recesos académicos de estudiantes, notificando directamente al Docente Asesor.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsTermCalculatorOpen(true);
                          if (!calculatedTermResult) handleCalculateLegalTerm();
                        }}
                        className="border-indigo-300 text-indigo-700 bg-indigo-50/70 hover:bg-indigo-100 font-bold text-xs h-8 px-3 shadow-2xs flex items-center gap-1.5 cursor-pointer"
                        title="Computar plazos en días hábiles conforme al CGP y festivos de Colombia"
                      >
                        <Timer className="h-3.5 w-3.5 text-indigo-600" />
                        <span>Calculadora de Términos</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setIsRadicadoModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-8 px-3 shadow-2xs"
                      >
                        <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                        <span>+ Vincular Radicado (23 dígitos)</span>
                      </Button>
                      <div className="text-right">
                        <span className="text-xs text-slate-400 block">Procesos monitoreados:</span>
                        <span className="text-lg font-black text-slate-800">{displayProcesses.length}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {loadingProcesses && !demoModeActive ? (
                      <div className="py-12 flex flex-col items-center justify-center text-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mb-2" />
                        <p className="text-xs text-slate-500">Sincronizando estados con la Rama Judicial...</p>
                      </div>
                    ) : displayProcesses && displayProcesses.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {displayProcesses.slice(0, 9).map((p: any) => (
                          <div
                            key={p.id}
                            className="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-xs transition-all flex flex-col justify-between"
                          >
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="text-[10px] font-mono font-bold text-slate-600 truncate">
                                  {p.radicado || "Radicado en trámite"}
                                </span>
                                <Badge className={cn(
                                  "text-[9.5px]",
                                  p.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600"
                                )}>
                                  {p.status === "active" ? "En curso" : "Finalizado"}
                                </Badge>
                              </div>
                              <h4 className="font-bold text-slate-800 text-xs mb-1 line-clamp-1">
                                {p.title || p.parties || "Proceso de Consultorio Jurídico"}
                              </h4>
                              <p className="text-[11px] text-slate-500 mb-2 line-clamp-1">
                                {p.despacho || "Juzgado de Pequeñas Causas"}
                              </p>
                            </div>
                            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10.5px]">
                              <span className="text-slate-400 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {p.last_action_date ? new Date(p.last_action_date).toLocaleDateString("es-CO") : "Sin alertas"}
                              </span>
                              <div className="flex items-center gap-2">
                                {(p.metadata?.hechos || p.hechos || p.metadata?.viabilidad_dictamen || p.viabilidad_dictamen) && (
                                  <button
                                    type="button"
                                    onClick={() => handleLoadCaseIntoStation(p)}
                                    className="text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200 transition-colors"
                                    title="Cargar hechos, viabilidad y borrador en la Estación Clínica"
                                  >
                                    ⚡ Retomar
                                  </button>
                                )}
                                <span
                                  onClick={() => setSelectedProcessForExpediente(p)}
                                  className="text-blue-600 font-bold hover:underline cursor-pointer"
                                >
                                  Ver expediente →
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-12 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-center">
                        <Briefcase className="h-8 w-8 text-slate-300 mb-2" />
                        <h4 className="text-xs font-bold text-slate-700">No hay procesos vinculados actualmente</h4>
                        <p className="text-[11px] text-slate-400 max-w-sm mt-1">
                          Los radicados asignados a los estudiantes en el módulo de Despachos se sincronizarán aquí para vigilancia 24/7.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* MODAL DIALOG PARA VINCULAR RADICADO */}
                <Dialog open={isRadicadoModalOpen} onOpenChange={(open) => {
                  setIsRadicadoModalOpen(open);
                  if (!open) {
                    setJudicialPreview(null);
                    setJudicialSearchError(null);
                  }
                }}>
                  <DialogContent className="sm:max-w-lg rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-blue-600" />
                        <span>Verificar y Vincular Proceso a Vigilancia 24/7</span>
                      </DialogTitle>
                      <DialogDescription className="text-xs text-slate-500">
                        Ingresa los 23 dígitos del radicado para contrastar y validar la información oficial con la Rama Judicial antes de activar el monitoreo.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      {/* Paso 1: Input Radicado y Botón de Búsqueda */}
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                        <label className="text-xs font-bold text-slate-800 block mb-1.5 flex items-center justify-between">
                          <span>Número de Radicado (23 dígitos):</span>
                          <span className="text-[10px] text-slate-400 font-normal">
                            {newRadicadoNum.replace(/\s+/g, "").length} / 23 dígitos
                          </span>
                        </label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Ej: 11001400302520260012300"
                            value={newRadicadoNum}
                            onChange={(e) => {
                              setNewRadicadoNum(e.target.value);
                              setJudicialPreview(null);
                              setJudicialSearchError(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleVerifyRadicado();
                              }
                            }}
                            className="font-mono text-xs h-9 border-slate-300 focus:border-blue-500 bg-white"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleVerifyRadicado}
                            disabled={isSearchingJudicial || newRadicadoNum.replace(/\s+/g, "").length < 10}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shrink-0"
                          >
                            {isSearchingJudicial ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                              <Search className="h-3.5 w-3.5 mr-1" />
                            )}
                            <span>Consultar Rama</span>
                          </Button>
                        </div>
                      </div>

                      {/* Estado de error / no encontrado */}
                      {judicialSearchError && (
                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold">No se pudo verificar automáticamente:</p>
                            <p className="text-[11px] text-amber-700 mt-0.5">{judicialSearchError}</p>
                          </div>
                        </div>
                      )}

                      {/* Tarjeta de Verificación Oficial desde la Rama Judicial */}
                      {judicialPreview && (
                        <div className="p-3.5 rounded-xl bg-emerald-50/60 border border-emerald-300 space-y-2.5 animate-in fade-in duration-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              <span>Información Oficial Verificada (Rama Judicial / CPNU)</span>
                            </div>
                            <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">
                              {judicialPreview.estado || "VIGENTE"}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 bg-white/80 p-2.5 rounded-lg border border-emerald-200">
                            <div className="flex items-start gap-2">
                              <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-900 block text-[11px]">Despacho Judicial:</span>
                                <span className="text-slate-700">{judicialPreview.juzgado || judicialPreview.despacho || "No especificado"}</span>
                              </div>
                            </div>

                            <div className="flex items-start gap-2">
                              <Scale className="h-3.5 w-3.5 text-indigo-600 shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold text-slate-900 block text-[11px]">Tipo de Proceso / Asunto:</span>
                                <span className="text-slate-700">{judicialPreview.tipo_proceso || judicialPreview.clase || "Proceso Judicial"}</span>
                              </div>
                            </div>

                            {(judicialPreview.demandante || judicialPreview.demandado) && (
                              <div className="flex items-start gap-2">
                                <UserCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-bold text-slate-900 block text-[11px]">Sujetos Procesales:</span>
                                  <p className="text-slate-700">
                                    <strong className="text-slate-800">Demandante:</strong> {typeof judicialPreview.demandante === "string" ? judicialPreview.demandante : "N/A"}
                                  </p>
                                  <p className="text-slate-700">
                                    <strong className="text-slate-800">Demandado:</strong> {typeof judicialPreview.demandado === "string" ? judicialPreview.demandado : "N/A"}
                                  </p>
                                </div>
                              </div>
                            )}

                            {(judicialPreview as any).actuaciones && (judicialPreview as any).actuaciones.length > 0 && (
                              <div className="pt-1 border-t border-slate-100 text-[10px] text-slate-500">
                                <span className="font-semibold text-slate-700">Última actuación: </span>
                                {(judicialPreview as any).actuaciones[0].fecha} — {(judicialPreview as any).actuaciones[0].actuacion}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Campos de Asignación Interna del Consultorio */}
                      <div className="space-y-3 pt-1">
                        <div>
                          <label className="text-xs font-bold text-slate-700 block mb-1">
                            Carátula / Título del Caso (Consultorio):
                          </label>
                          <Input
                            placeholder="Ej: Restrepo vs. Distribuciones del Valle"
                            value={newRadicadoTitle}
                            onChange={(e) => setNewRadicadoTitle(e.target.value)}
                            className="text-xs h-9 border-slate-300"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-700 block mb-1">
                            Estudiante Practicante Asignado:
                          </label>
                          <Input
                            placeholder="Ej: Juan Camilo Pérez (Semestre X)"
                            value={newRadicadoStudent}
                            onChange={(e) => setNewRadicadoStudent(e.target.value)}
                            className="text-xs h-9 border-slate-300"
                          />
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsRadicadoModalOpen(false)}
                        className="text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={addingRadicado || !newRadicadoNum.trim() || (!judicialPreview && !judicialSearchError)}
                        onClick={handleAddProcessToClinic}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs"
                      >
                        {addingRadicado ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                        <span>Confirmar y Vincular a Vigilancia 24/7</span>
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              /* MÓDULOS 1, 2 Y 3: VISTA SPLIT-SCREEN (ENTRADA + DICTAMEN) */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 h-full">

                {/* PANEL IZQUIERDO: FORMULARIOS DE ENTRADA */}
                <div className="lg:col-span-5 flex flex-col gap-4">
                  {activeStation === "socratic" && (
                    /* ESTACIÓN 1: TALLER SOCRÁTICO */
                    <Card className="border-slate-200/90 shadow-xs flex flex-col flex-1">
                      <CardHeader className="pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">
                            Metodología Anti-Plagio
                          </Badge>
                          <div className="flex items-center gap-2">
                            {(socraticFacts || socraticThesis || socraticThread.length > 0) && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleResetSocratic}
                                className="h-6 text-[10px] px-2 text-slate-500 hover:text-slate-800"
                                title="Limpiar taller para iniciar un nuevo debate socrático"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Nuevo Planteamiento
                              </Button>
                            )}
                            <span className="text-[11px] font-semibold text-slate-400">Matriz Dogmática</span>
                          </div>
                        </div>
                        <CardTitle className="text-base text-slate-800 mt-2">
                          Auditor de Hipótesis y Silogismo Dogmático
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Estructura tu problema jurídico, fuentes y postura antes de recibir la auditoría socrática.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4 flex flex-col gap-3 flex-1">
                        <div className="p-2.5 rounded-lg bg-slate-50/90 border border-slate-200/80 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700">
                              I. Delimitación Dogmática & Problema Jurídico
                            </span>
                            <Badge variant="outline" className="text-[9.5px] bg-white text-slate-600">Saber Pro / Preparatorios</Badge>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Área / Asignatura:</label>
                              <select
                                value={socraticMatter}
                                onChange={(e) => setSocraticMatter(e.target.value)}
                                className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                              >
                                <option value="Derecho Civil / Obligaciones">Derecho Civil / Obligaciones</option>
                                <option value="Derecho Laboral Individual">Derecho Laboral & Seg. Social</option>
                                <option value="Derecho Procesal General (CGP)">Derecho Procesal General (CGP)</option>
                                <option value="Constitucional y Acciones de Tutela">Constitucional & Tutela</option>
                                <option value="Derecho Administrativo / Contencioso">Derecho Administrativo (CPACA)</option>
                                <option value="Derecho Penal / Teoría del Delito">Derecho Penal & Garantías</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Normas / Sentencias que invocas:</label>
                              <Input
                                placeholder="Ej: Art. 64 CST, SU-049/17..."
                                value={socraticSources}
                                onChange={(e) => setSocraticSources(e.target.value)}
                                className="h-8 text-xs border-slate-200 bg-white"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-[11px] font-bold text-slate-700 block mb-1">
                              Problema Jurídico Formulado por ti (`¿...?`):
                            </label>
                            <Input
                              placeholder="Ej: ¿Es eficaz el despido sin justa causa de un trabajador con fuero de salud sin permiso del MinTrabajo?"
                              value={socraticIssue}
                              onChange={(e) => setSocraticIssue(e.target.value)}
                              className="h-8 text-xs border-slate-200 bg-white"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-700 block mb-1">
                            II. Hechos Jurídicamente Relevantes del Caso:
                          </label>
                          <Textarea
                            placeholder="Describe la situación fáctica, fechas clave, contratos o actuaciones procesales..."
                            value={socraticFacts}
                            onChange={(e) => setSocraticFacts(e.target.value)}
                            rows={3}
                            className="text-xs resize-none border-slate-200 focus:border-indigo-400"
                          />
                        </div>

                        <div>
                          <label className="text-xs font-bold text-slate-700 flex items-center justify-between mb-1">
                            <span>III. Tu Tesis Jurídica o Subsunción Propuesta:</span>
                            <span className="text-[10px] text-indigo-600 font-semibold">(Obligatorio Anti-Plagio)</span>
                          </label>
                          <Textarea
                            placeholder="Defiende tu postura jurídica explicando cómo se articula la norma con los hechos del caso..."
                            value={socraticThesis}
                            onChange={(e) => setSocraticThesis(e.target.value)}
                            rows={3}
                            className="text-xs resize-none border-slate-200 focus:border-indigo-400 bg-indigo-50/30"
                          />
                        </div>

                        <div className="pt-0.5">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1.5">
                            Casos de Taller Precargados (1 Clic):
                          </span>
                          <div className="flex flex-col gap-1.5">
                            {SOCRATIC_EXAMPLES.map((ex, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  setSocraticMatter(ex.matter);
                                  setSocraticIssue(ex.issue);
                                  setSocraticSources(ex.sources);
                                  setSocraticFacts(ex.facts);
                                  setSocraticThesis(ex.studentThesis);
                                }}
                                className="text-left p-2 rounded-lg border border-slate-100 bg-slate-50 hover:bg-indigo-50/60 hover:border-indigo-200 transition-all text-xs group"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-slate-800 text-[11px] group-hover:text-indigo-700">
                                    {ex.title}
                                  </span>
                                  <span className="text-[9.5px] text-slate-400">{ex.matter}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <Button
                          onClick={handleRunSocratic}
                          disabled={socraticLoading || !socraticFacts.trim()}
                          className="w-full mt-auto bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold text-xs py-2.5 shadow-sm"
                        >
                          {socraticLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              <span>Auditando Criterio & Reconstruyendo Precedente...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              <span>Auditar Criterio y Generar Silogismo</span>
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {activeStation === "clinic" && (
                    /* ESTACIÓN 2: CONSULTORIO JURÍDICO CLÍNICO */
                    <Card className="border-slate-200/90 shadow-xs flex flex-col flex-1">
                      <CardHeader className="pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-300">
                              Ley 2113 de 2021 • SICAJ
                            </Badge>
                            {activeCaseId && (
                              <Badge variant="outline" className="text-[10px] font-mono text-emerald-700 bg-emerald-50/50 border-emerald-200">
                                {activeCaseId}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {(clinicClaimant || clinicFacts) && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleResetForNewCase}
                                className="h-6 text-[10px] px-2 text-slate-500 hover:text-slate-800"
                                title="Limpiar campos para iniciar un nuevo caso sin borrar los casos guardados"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Nuevo Caso
                              </Button>
                            )}
                            <span className="text-[11px] font-semibold text-slate-400">Protocolo Entrevista</span>
                          </div>
                        </div>
                        <CardTitle className="text-base text-slate-800 mt-2">
                          Ficha Forense de Recepción y Competencia Clínica
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Caracterización socioeconómica (Art. 8 Ley 2113), control de topes SMLMV y acervo probatorio.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-3.5 flex flex-col gap-3 flex-1">
                        {/* BLOQUE I: SUJETO DE ESPECIAL PROTECCIÓN & FOCALIZACIÓN SOCIOECONÓMICA */}
                        <div className="p-2.5 rounded-lg bg-slate-50/90 border border-slate-200/80 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">
                              I. Caracterización del Usuario (Art. 6 y 8 Ley 2113)
                            </span>
                            <span className="text-[9.5px] font-semibold text-slate-400">Reporte MinJusticia</span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Nombre del Solicitante:</label>
                              <Input
                                placeholder="Ej: Carlos Mario Restrepo"
                                value={clinicClaimant}
                                onChange={(e) => setClinicClaimant(e.target.value)}
                                className="text-xs h-8 border-slate-200 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Documento (C.C. / PPT / NIT):</label>
                              <Input
                                placeholder="Ej: C.C. 1.020.455.891"
                                value={clinicUserDoc}
                                onChange={(e) => setClinicUserDoc(e.target.value)}
                                className="text-xs h-8 border-slate-200 bg-white"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                                Estrato Socioeconómico:
                              </label>
                              <select
                                value={clinicEstrato}
                                onChange={(e) => setClinicEstrato(parseInt(e.target.value) || 1)}
                                className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                              >
                                <option value={1}>Estrato 1 (Prioritario)</option>
                                <option value={2}>Estrato 2 (Prioritario)</option>
                                <option value={3}>Estrato 3 (Vulnerable)</option>
                                <option value={4}>Estrato 4 (Requiere Sujeto Especial)</option>
                                <option value={5}>Estrato 5 (No focalizado)</option>
                                <option value={6}>Estrato 6 (No focalizado)</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                                Enfoque Diferencial / Vulnerabilidad:
                              </label>
                              <select
                                value={clinicVulnerability}
                                onChange={(e) => setClinicVulnerability(e.target.value)}
                                className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                              >
                                {VULNERABILITY_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* BLOQUE II: CONTRAPARTE, CUANTÍA Y PRESUPUESTOS PROCESALES */}
                        <div className="p-2.5 rounded-lg bg-slate-50/90 border border-slate-200/80 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-700">
                              II. Contraparte, Cuantía y Presupuestos Procesales
                            </span>
                            <span className="text-[9.5px] font-mono text-emerald-700">Art. 9 Ley 2113</span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Materia / Área Clínica:</label>
                              <select
                                value={clinicCategory}
                                onChange={(e) => setClinicCategory(e.target.value)}
                                className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                              >
                                <option value="Laboral">Laboral & Seg. Social (≤ 20 SMLMV)</option>
                                <option value="Civil">Civil / Comercial (≤ 40 SMLMV)</option>
                                <option value="Familia">Familia / Alimentos / Custodia</option>
                                <option value="Penal">Penal Querellable (Art. 74 CPP)</option>
                                <option value="Constitucional">Acción de Tutela / Salud</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1 flex items-center justify-between">
                                <span>Cuantía (COP o SMLMV):</span>
                                {parseCopAmount(clinicAmount) > 0 && (
                                  <span className="text-[9.5px] text-emerald-700 font-mono">
                                    ≈ {(parseCopAmount(clinicAmount) / 1550000).toFixed(1)} SMLMV (${(parseCopAmount(clinicAmount) / 1000000).toFixed(1)}M)
                                  </span>
                                )}
                              </label>
                              <Input
                                placeholder="Ej: 14.5 SMLMV o $18.000.000"
                                value={clinicAmount}
                                onChange={(e) => setClinicAmount(e.target.value)}
                                className="text-xs h-8 border-slate-200 bg-white"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Accionado / Contraparte:</label>
                              <Input
                                placeholder="Empresa, EPS, Empleador o Demandado..."
                                value={clinicCounterparty}
                                onChange={(e) => setClinicCounterparty(e.target.value)}
                                className="text-xs h-8 border-slate-200 bg-white"
                              />
                            </div>

                            <div>
                              <label className="text-[11px] font-bold text-slate-700 block mb-1">Fecha Hechos (Prescripción):</label>
                              <Input
                                type="date"
                                value={clinicIncidentDate}
                                onChange={(e) => setClinicIncidentDate(e.target.value)}
                                className="text-xs h-8 border-slate-200 bg-white"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-[11px] font-bold text-slate-700 block mb-1">Requisito de Procedibilidad / Vía Previa:</label>
                            <select
                              value={clinicProceduralReq}
                              onChange={(e) => setClinicProceduralReq(e.target.value)}
                              className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                            >
                              <option value="Pendiente agotar conciliación / reclamación">Pendiente agotar conciliación o reclamación previa</option>
                              <option value="Conciliación extrajudicial fallida (Constancia)">Conciliación extrajudicial fallida (Con constancia)</option>
                              <option value="Derecho de petición / Reclamación radicada sin respuesta">Derecho de petición / Reclamación radicada sin respuesta</option>
                              <option value="No aplica / Acción de Tutela o Medida Cautelar">No aplica (Acción de Tutela / Solicitud medidas cautelares)</option>
                            </select>
                          </div>
                        </div>

                        {/* SEMÁFORO DE COMPETENCIA LEY 2113 DE 2021 */}
                        {competenceResult && (
                          <div className={`p-3 rounded-lg border text-xs ${competenceResult.decision === "COMPETENTE"
                              ? "bg-emerald-50/80 border-emerald-200 text-emerald-900"
                              : competenceResult.decision === "NO_COMPETENTE"
                                ? "bg-rose-50/80 border-rose-200 text-rose-900"
                                : "bg-amber-50/80 border-amber-200 text-amber-900"
                            }`}>
                            <div className="flex items-center justify-between font-bold mb-1">
                              <span className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full animate-pulse" style={{
                                  backgroundColor: competenceResult.decision === "COMPETENTE" ? "#10b981" : competenceResult.decision === "NO_COMPETENTE" ? "#f43f5e" : "#f59e0b"
                                }} />
                                {competenceResult.decision === "COMPETENTE" && "COMPETENCIA PLENA DE CONSULTORIO"}
                                {competenceResult.decision === "NO_COMPETENTE" && "ASUNTO NO COMPETENTE — REMISIÓN OBLIGATORIA"}
                                {competenceResult.decision === "OBSERVACION" && "COMPETENCIA CONDICIONADA / OBSERVACIÓN"}
                              </span>
                              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-white/70 border">
                                {competenceResult.cuantia_smlmv > 0 ? `${competenceResult.cuantia_smlmv} SMLMV` : "Sin Límite Cuantía"}
                              </span>
                            </div>
                            <p className="text-[11px] leading-relaxed mb-1.5 text-slate-700">
                              {competenceResult.motivo}
                            </p>
                            <p className="text-[10px] font-semibold text-slate-500">
                              <strong>Fundamento:</strong> {competenceResult.fundamento_legal}
                            </p>
                            {competenceResult.decision === "NO_COMPETENTE" && competenceResult.remision_sugerida && (
                              <div className="mt-2 pt-2 border-t border-rose-200/80 flex items-center justify-between">
                                <span className="text-[10.5px] font-bold text-rose-800">
                                  Remitir a: {competenceResult.remision_sugerida}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[10px] bg-white border-rose-300 text-rose-700 hover:bg-rose-100"
                                  onClick={() => {
                                    toast.info(`Generando oficio de remisión a ${competenceResult.remision_sugerida}...`);
                                    setClinicFacts(prev => `[OFICIO DE REMISIÓN INSTITUCIONAL POR FALTA DE COMPETENCIA LEY 2113]\n` +
                                      `Entidad de Remisión: ${competenceResult.remision_sugerida}\n` +
                                      `Motivo Técnico: ${competenceResult.motivo}\n\n` + prev);
                                  }}
                                >
                                  Emitir Remisión
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* BLOQUE III: SOPORTES PROBATORIOS APORTADOS Y RELATO */}
                        <div>
                          <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between mb-1.5">
                            <span>III. Soportes Aportados por el Usuario en Entrevista:</span>
                            <span className="text-[10px] text-emerald-700 font-semibold">{clinicEvidences.length} seleccionados</span>
                          </label>
                          <div className="flex flex-wrap gap-1.5 mb-2.5">
                            {EVIDENCE_CHECKLIST_OPTIONS.map((ev) => {
                              const active = clinicEvidences.includes(ev);
                              return (
                                <button
                                  key={ev}
                                  type="button"
                                  onClick={() => toggleClinicEvidence(ev)}
                                  className={cn(
                                    "px-2 py-1 rounded-md text-[10.5px] font-semibold border transition-all cursor-pointer",
                                    active
                                      ? "bg-emerald-100/90 border-emerald-400 text-emerald-950 shadow-2xs"
                                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                                  )}
                                >
                                  {active ? "✓ " : "+ "}{ev}
                                </button>
                              );
                            })}
                          </div>

                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            Relato Fáctico Cronológico del Usuario:
                          </label>
                          <Textarea
                            placeholder="Describe los hechos narrados en recepción (fechas, salarios, incumplimientos o vulneraciones)..."
                            value={clinicFacts}
                            onChange={(e) => setClinicFacts(e.target.value)}
                            rows={3}
                            className="text-xs resize-none border-slate-200 focus:border-emerald-500"
                          />
                        </div>

                        <div className="pt-0.5">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                            Casos Tipo de Consultorio (Poblar Ficha en 1 Clic):
                          </span>
                          <div className="flex flex-col gap-1.5">
                            {CLINIC_EXAMPLES.map((ex, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  setClinicClaimant(ex.claimant);
                                  setClinicUserDoc(ex.userDoc);
                                  setClinicEstrato(ex.estrato);
                                  setClinicVulnerability(ex.vulnerability);
                                  setClinicCounterparty(ex.counterparty);
                                  setClinicIncidentDate(ex.incidentDate);
                                  setClinicProceduralReq(ex.proceduralReq);
                                  setClinicEvidences(ex.evidences);
                                  setClinicCategory(ex.category);
                                  setClinicAmount(ex.amount);
                                  setClinicFacts(ex.summary);
                                  setCompetenceResult(null);
                                }}
                                className="text-left p-2 rounded-lg border border-slate-100 bg-slate-50 hover:bg-emerald-50/60 hover:border-emerald-200 transition-all text-xs group"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-slate-800 text-[11px] group-hover:text-emerald-800">
                                    {ex.title}
                                  </span>
                                  <span className="text-[9.5px] font-semibold text-emerald-700">{ex.amount}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <Button
                          onClick={handleRunClinic}
                          disabled={clinicLoading || !clinicFacts.trim()}
                          className="w-full mt-auto bg-gradient-to-r from-emerald-700 to-teal-700 hover:from-emerald-800 hover:to-teal-800 text-white font-bold text-xs py-2.5 shadow-sm"
                        >
                          {clinicLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              <span>Validando Competencia Ley 2113/2021...</span>
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              <span>Emitir Ficha Clínica y Control de Competencia</span>
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {activeStation === "citations" && (
                    /* ESTACIÓN 3: AUDITOR DE INTEGRIDAD Y CITAS */
                    <Card className="border-slate-200/90 shadow-xs flex flex-col flex-1">
                      <CardHeader className="pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-rose-50 text-rose-700 border-rose-200">
                            Control de Alucinaciones
                          </Badge>
                          <span className="text-[11px] font-semibold text-slate-400">Verificación Oficial</span>
                        </div>
                        <CardTitle className="text-base text-slate-800 mt-2">
                          Auditor de Ensayos, Monografías y Citas
                        </CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Detecta citas inventadas por ChatGPT, normas derogadas y sentencias sacadas de contexto.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4 flex flex-col gap-3.5 flex-1">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-bold text-slate-700">
                              Pega el fragmento o carga tu archivo Word (.docx / .txt):
                            </label>
                            <div>
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".docx,.txt"
                                className="hidden"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={uploadingDoc}
                                onClick={() => fileInputRef.current?.click()}
                                className="h-6 text-[11px] px-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                              >
                                {uploadingDoc ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                                <span>Subir .docx / .txt</span>
                              </Button>
                            </div>
                          </div>
                          <Textarea
                            placeholder="Pega aquí el contenido con las citas jurisprudenciales o artículos que deseas auditar, o sube tu archivo arriba..."
                            value={auditText}
                            onChange={(e) => setAuditText(e.target.value)}
                            rows={6}
                            className="text-xs resize-none border-slate-200 focus:border-rose-400"
                          />
                        </div>

                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1.5">
                            Ejemplos con Citas Reales vs. Alucinadas:
                          </span>
                          <div className="flex flex-col gap-1.5">
                            {CITATION_AUDIT_EXAMPLES.map((ex, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setAuditText(ex.text)}
                                className="text-left p-2 rounded-lg border border-slate-100 bg-slate-50 hover:bg-rose-50/60 hover:border-rose-200 transition-all text-xs group"
                              >
                                <span className="font-bold text-slate-800 text-[11px] group-hover:text-rose-700 block">
                                  {ex.title}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <Button
                          onClick={handleRunAudit}
                          disabled={auditLoading || !auditText.trim()}
                          className="w-full mt-auto bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 text-white font-bold text-xs py-2.5 shadow-sm"
                        >
                          {auditLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              <span>Cotejando con Relatorías de Altas Cortes...</span>
                            </>
                          ) : (
                            <>
                              <SearchCheck className="h-4 w-4 mr-2" />
                              <span>Auditar Integridad Jurisprudencial</span>
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* PANEL DERECHO: DICTAMEN ESTRUCTURADO Y EXPORTACIÓN */}
                <div className="lg:col-span-7 flex flex-col h-full">
                  <Card className="border-slate-200/90 shadow-xs flex flex-col flex-1 h-full min-h-[550px] overflow-hidden">
                    <CardHeader className="py-3 px-4 border-b border-slate-100 bg-slate-50/60 flex flex-row items-center justify-between shrink-0">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-2.5 h-2.5 rounded-full animate-pulse",
                          (socraticLoading || clinicLoading || auditLoading)
                            ? "bg-amber-500"
                            : (socraticOutput || clinicOutput || auditOutput)
                              ? "bg-emerald-500"
                              : "bg-slate-300"
                        )} />
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xs sm:text-sm font-bold text-slate-800">
                            {activeStation === "socratic"
                              ? "Dictamen Dogmático & Evaluación Socrática"
                              : activeStation === "clinic"
                                ? "Ficha Técnica y Matriz Probatoria (Docente Asesor)"
                                : "Dictamen de Integridad & Auditoría de Citas"}
                          </CardTitle>
                          <Badge variant="outline" className="text-[10px] font-semibold text-slate-500 border-slate-200 bg-white hidden sm:inline-flex">
                            Paso 2: Dictamen & Evaluación
                          </Badge>
                          {activeStation === "clinic" && (
                            <>
                              <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] hidden md:inline-flex">
                                Documentos Facultad: {completedDocsCount}/5
                              </Badge>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setIsAppointmentModalOpen(true)}
                                className="h-6 text-[10.5px] px-2 text-indigo-700 border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100 flex items-center gap-1 cursor-pointer"
                                title="¿Tienes dudas con el proceso? Pide cita con el docente o asistente"
                              >
                                <CalendarPlus className="h-3 w-3 text-indigo-600" />
                                <span>Pedir Cita / Dudas</span>
                              </Button>
                            </>
                          )}
                        </div>

                        {/* INDICADORES RÁPIDOS DE SUSCRIPCIÓN EN CABECERA */}
                        {(activeStation === "socratic" || activeStation === "clinic") && (
                          <div className="hidden md:flex items-center gap-1.5 ml-2">
                            <button
                              type="button"
                              onClick={() => {
                                setStationSignRole("estudiante");
                                setStationSignContext(activeStation === "socratic" ? "socratic" : "clinic");
                                setIsStationSignOpen(true);
                              }}
                              className="cursor-pointer"
                              title="Firmar como Practicante"
                            >
                              {stationStudentSignature ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9.5px] hover:bg-emerald-200 transition-colors">
                                  ✓ Practicante Firmó
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-700 border-amber-300 text-[9.5px] hover:bg-amber-50 transition-colors">
                                  ✍ Firma Practicante
                                </Badge>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setStationSignRole("docente");
                                setStationSignContext(activeStation === "socratic" ? "socratic" : "clinic");
                                setIsStationSignOpen(true);
                              }}
                              className="cursor-pointer"
                              title="Firmar como Docente Tutor (Vo.Bo.)"
                            >
                              {stationTutorSignature ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9.5px] hover:bg-emerald-200 transition-colors">
                                  ✓ Vo.Bo. Docente
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-indigo-700 border-indigo-300 text-[9.5px] hover:bg-indigo-50 transition-colors">
                                  ⚖ Vo.Bo. Docente
                                </Badge>
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* ACCIONES DE COPIA Y EXPORTACIÓN A WORD */}
                      <div className="flex items-center gap-1.5">
                        {(activeStation === "socratic" ? socraticOutput : activeStation === "clinic" ? clinicOutput : auditOutput) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  activeStation === "socratic"
                                    ? (socraticOutput || "")
                                    : activeStation === "clinic"
                                      ? (clinicOutput || "")
                                      : (auditOutput || "")
                                )
                              }
                              className="h-7 text-xs px-2.5 text-slate-600 hover:text-slate-900"
                            >
                              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                              <span>{copied ? "Copiado" : "Copiar"}</span>
                            </Button>

                            {activeStation === "clinic" && clinicOutput && (
                              <>
                                <Button
                                  variant="default"
                                  size="sm"
                                  disabled={isGeneratingDraft}
                                  onClick={handleGenerateLegalDraft}
                                  className="h-7 text-xs px-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-bold flex items-center gap-1 shadow-xs cursor-pointer"
                                >
                                  {isGeneratingDraft ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <FileEdit className="h-3.5 w-3.5" />
                                  )}
                                  <span>{isGeneratingDraft ? "Proyectando..." : "Borrador Jurídico"}</span>
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsConciliacionModalOpen(true)}
                                  className="h-7 text-xs px-2.5 border-emerald-300 text-emerald-800 bg-emerald-50/70 hover:bg-emerald-100 font-bold flex items-center gap-1 shadow-2xs cursor-pointer"
                                  title="Emitir Acta de Acuerdo o Constancia de Conciliación (Ley 2220 de 2022)"
                                >
                                  <Scale className="h-3.5 w-3.5 text-emerald-700" />
                                  <span>Acta Conciliación</span>
                                </Button>
                              </>
                            )}

                            <Button
                              variant="default"
                              size="sm"
                              disabled={exportingDocx}
                              onClick={() =>
                                exportToDocx(
                                  activeStation === "socratic"
                                    ? "Dictamen de Taller Socrático y Silogismo Dogmático"
                                    : activeStation === "clinic"
                                      ? "Ficha de Consultorio Jurídico - Ley 2113 de 2021"
                                      : "Dictamen de Integridad de Citas Jurisprudenciales",
                                  (activeStation === "socratic"
                                    ? socraticOutput
                                    : activeStation === "clinic"
                                      ? clinicOutput
                                      : auditOutput) || "",
                                  activeStation
                                )
                              }
                              className="h-7 text-xs px-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-1"
                            >
                              {exportingDocx ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Download className="h-3.5 w-3.5" />
                              )}
                              <span>Word (.docx)</span>
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.print()}
                              className="h-7 text-xs px-2 text-slate-600 hover:text-slate-900 hidden sm:flex"
                              title="Imprimir para expediente"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardHeader>

                    {/* CONTENIDO DEL REPORTE */}
                    <CardContent className="p-4 sm:p-6 flex-1 overflow-y-auto custom-scrollbar bg-white">
                      {(socraticLoading || clinicLoading || auditLoading) ? (
                        <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                          <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-3 animate-bounce">
                            <Loader2 className="h-6 w-6 animate-spin" />
                          </div>
                          <h3 className="text-sm font-bold text-slate-800">
                            {activeStation === "socratic"
                              ? "Auditando hipótesis y reconstruyendo línea jurisprudencial..."
                              : activeStation === "clinic"
                                ? "Verificando competencia por cuantía y requisitos Ley 2113 de 2021..."
                                : "Cotejando citas contra relatoría oficial de Altas Cortes..."}
                          </h3>
                          <p className="text-xs text-slate-400 mt-1 max-w-sm">
                            {activeStation === "socratic" ? socraticTrace : activeStation === "clinic" ? clinicTrace : auditTrace}
                          </p>
                        </div>
                      ) : (activeStation === "socratic" ? socraticOutput : activeStation === "clinic" ? clinicOutput : auditOutput) ? (
                        <div ref={outputRef} className="space-y-4 animate-in fade-in duration-300">
                          {/* BANNER DE IDENTIFICACIÓN */}
                          <div className={cn(
                            "p-3 rounded-xl border flex items-center justify-between text-xs",
                            activeStation === "socratic"
                              ? "bg-indigo-50/70 border-indigo-200/80 text-indigo-950"
                              : activeStation === "clinic"
                                ? "bg-emerald-50/70 border-emerald-200/80 text-emerald-950"
                                : "bg-rose-50/70 border-rose-200/80 text-rose-950"
                          )}>
                            <div className="flex items-center gap-2">
                              <FileCheck className="h-4 w-4 shrink-0 text-emerald-700" />
                              <span className="font-semibold">
                                {activeStation === "socratic"
                                  ? "Evaluación Formativa Emitida — Taller de Criterio Jurídico"
                                  : activeStation === "clinic"
                                    ? "Pre-Auditoría Lista para Visto Bueno del Docente Asesor"
                                    : "Dictamen de Cotejo Jurisprudencial Emitido"}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-[10px] bg-white border-slate-200 text-slate-700">
                              {new Date().toLocaleDateString("es-CO")}
                            </Badge>
                          </div>

                          {/* BANNER DESTACADO PARA GENERAR BORRADOR JUDICIAL */}
                          {activeStation === "clinic" && (
                            <div className="p-3.5 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-blue-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                              <div className="flex items-start gap-2.5">
                                <div className="p-2 rounded-lg bg-emerald-600 text-white mt-0.5 shadow-xs">
                                  <ScrollText className="h-4 w-4" />
                                </div>
                                <div>
                                  <h4 className="text-xs font-bold text-slate-900">
                                    Viabilidad Acreditada — Proyección de Escrito Forense
                                  </h4>
                                  <p className="text-[11px] text-slate-600 mt-0.5">
                                    Genera el borrador formal de la demanda con partes, pretensiones cuantificadas, hechos numerados y técnica procesal de la Ley 2113.
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={handleGenerateLegalDraft}
                                disabled={isGeneratingDraft}
                                className="h-8 text-xs px-3.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold shrink-0 flex items-center gap-1.5 shadow-sm"
                              >
                                {isGeneratingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-amber-300" />}
                                <span>{isGeneratingDraft ? "Redactando memorial..." : "⚡ Proyectar Borrador Jurídico"}</span>
                              </Button>
                            </div>
                          )}

                          {/* CUERPO DEL DICTAMEN EN FORMATO LEGAL Y ESTRUCTURADO */}
                          <div className="prose prose-sm max-w-none text-slate-800 text-xs sm:text-[13px] leading-relaxed font-sans">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ node, ...props }) => (
                                  <h1 className="text-base sm:text-lg font-black text-slate-900 border-b border-slate-200 pb-1.5 mt-4 mb-2 tracking-tight" {...props} />
                                ),
                                h2: ({ node, ...props }) => (
                                  <h2 className="text-sm sm:text-base font-extrabold text-slate-800 border-b border-slate-100 pb-1 mt-3.5 mb-1.5 flex items-center gap-1.5" {...props} />
                                ),
                                h3: ({ node, ...props }) => (
                                  <h3 className="text-xs sm:text-sm font-bold text-slate-800 mt-2.5 mb-1 text-indigo-900" {...props} />
                                ),
                                p: ({ node, ...props }) => (
                                  <p className="mb-2.5 text-slate-700 leading-relaxed text-justify" {...props} />
                                ),
                                ul: ({ node, ...props }) => (
                                  <ul className="list-disc list-outside pl-4 mb-3 space-y-1 text-slate-700" {...props} />
                                ),
                                ol: ({ node, ...props }) => (
                                  <ol className="list-decimal list-outside pl-4 mb-3 space-y-1 text-slate-700 font-medium" {...props} />
                                ),
                                li: ({ node, ...props }) => (
                                  <li className="pl-0.5 leading-relaxed" {...props} />
                                ),
                                strong: ({ node, ...props }) => (
                                  <strong className="font-bold text-slate-900 bg-amber-50/60 px-0.5 rounded" {...props} />
                                ),
                                table: ({ node, ...props }) => (
                                  <div className="my-3 overflow-x-auto rounded-lg border border-slate-200 shadow-2xs">
                                    <table className="w-full text-left border-collapse text-xs" {...props} />
                                  </div>
                                ),
                                th: ({ node, ...props }) => (
                                  <th className="bg-slate-100 font-bold text-slate-800 p-2 border-b border-slate-200 text-[11px] uppercase tracking-wider" {...props} />
                                ),
                                td: ({ node, ...props }) => (
                                  <td className="p-2 border-b border-slate-100 text-slate-700 text-xs" {...props} />
                                ),
                                blockquote: ({ node, ...props }) => (
                                  <blockquote className="border-l-4 border-indigo-500 bg-indigo-50/40 px-3 py-2 rounded-r-lg my-2 text-slate-700 italic text-xs" {...props} />
                                ),
                                code: ({ node, inline, ...props }: any) => (
                                  inline ? (
                                    <code className="bg-slate-100 text-indigo-700 font-mono text-[11px] px-1 py-0.5 rounded" {...props} />
                                  ) : (
                                    <div className="bg-slate-900 text-slate-100 p-3 rounded-lg font-mono text-xs my-2 overflow-x-auto" {...props} />
                                  )
                                )
                              }}
                            >
                              {stripFollowUpQuestions(
                                activeStation === "socratic"
                                  ? socraticOutput
                                  : activeStation === "clinic"
                                    ? clinicOutput
                                    : auditOutput
                              )}
                            </ReactMarkdown>
                          </div>

                          {/* CAJA DE RÉPLICA AL RETO SOCRÁTICO MULTI-TURNO */}
                          {activeStation === "socratic" && socraticOutput && (
                            <div className="mt-6 pt-4 border-t border-indigo-100 bg-indigo-50/50 p-4 rounded-xl border border-indigo-200">
                              {/* TARJETA DE CALIFICACIÓN FORMATIVA SI YA FUE EVALUADA */}
                              {socraticGrading && (
                                <div className="mb-4 p-4 rounded-xl bg-white border border-indigo-200 shadow-sm space-y-3">
                                  <div className="flex items-center justify-between border-b border-indigo-100 pb-2">
                                    <div className="flex items-center gap-2">
                                      <Award className="h-5 w-5 text-indigo-700" />
                                      <div>
                                        <h4 className="text-xs font-bold text-slate-900">Dictamen de Evaluación Formativa — Criterio Dogmático</h4>
                                        <p className="text-[10.5px] text-slate-500">Ley 2113 de 2021 — Práctica Formativa Universitaria</p>
                                      </div>
                                    </div>
                                    <Badge className="bg-indigo-600 text-white font-mono text-xs px-2.5 py-0.5">
                                      Nota: {socraticGrading.final_score} / 5.0
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                                      <span className="text-[10px] text-slate-500 block font-bold">SUBSUNCIÓN Y SILOGISMO:</span>
                                      <span className="font-extrabold text-indigo-900 text-sm">{socraticGrading.score_subsun} / 5.0</span>
                                    </div>
                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                                      <span className="text-[10px] text-slate-500 block font-bold">MANEJO DE EXCEPCIONES:</span>
                                      <span className="font-extrabold text-indigo-900 text-sm">{socraticGrading.score_excepciones} / 5.0</span>
                                    </div>
                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                                      <span className="text-[10px] text-slate-500 block font-bold">SOLIDEZ ARGUMENTATIVA:</span>
                                      <span className="font-extrabold text-indigo-900 text-sm">{socraticGrading.score_argumentacion} / 5.0</span>
                                    </div>
                                  </div>
                                  <div className="text-xs bg-indigo-50/60 p-2.5 rounded-lg border border-indigo-100 text-slate-700">
                                    <strong className="text-indigo-950 block mb-0.5">Concepto Formativo del Tutor IA:</strong>
                                    <p className="text-[11.5px] leading-relaxed">{socraticGrading.feedback_pedagogico}</p>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] text-emerald-700 font-semibold pt-1">
                                    <span>✓ {socraticGrading.horas_acreditadas} Horas formativas acreditadas al censo SICAJ</span>
                                    <Badge variant="outline" className="text-[10px] border-emerald-300 bg-emerald-50 text-emerald-800">
                                      Nivel: {socraticGrading.nivel_dominio}
                                    </Badge>
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4 text-indigo-700" />
                                  <span className="font-bold text-xs text-indigo-950">
                                    Responder al Reto Socrático de Variación Fáctica:
                                  </span>
                                </div>
                                <Badge variant="outline" className="text-[10px] bg-white text-indigo-700 border-indigo-300">
                                  Turno {Math.floor(socraticThread.length / 2) + 1}
                                </Badge>
                              </div>
                              <Textarea
                                placeholder="Escribe tu argumentación jurídica frente al reto hipotético planteado por el tutor IA (excepción aplicable, variación de la subsunción o carga probatoria)..."
                                value={socraticFollowUp}
                                onChange={(e) => setSocraticFollowUp(e.target.value)}
                                rows={3}
                                className="text-xs bg-white border-indigo-200 focus:border-indigo-500 mb-2.5"
                              />
                              <div className="flex justify-between items-center">
                                <span className="text-[10.5px] text-indigo-700/80 italic">
                                  El Tutor IA evaluará tu capacidad de refutación dogmática en contexto.
                                </span>
                                <div className="flex items-center gap-2">
                                  {socraticThread.length >= 3 && !socraticGrading && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={isGradingSocratic || socraticLoading}
                                      onClick={handleGradeSocraticSession}
                                      className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-bold"
                                    >
                                      {isGradingSocratic ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Award className="h-3.5 w-3.5 mr-1.5" />}
                                      <span>Finalizar y Calificar Sesión</span>
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    disabled={socraticLoading || !socraticFollowUp.trim()}
                                    onClick={handleReplySocraticChallenge}
                                    className="bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-xs h-8 px-4 shadow-xs"
                                  >
                                    {socraticLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                                    <span>Enviar Réplica al Tutor IA</span>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                        </div>
                      ) : (
                        /* ESTADO VACÍO ILUSTRATIVO */
                        <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                            {activeStation === "socratic" ? (
                              <BookOpen className="h-7 w-7 text-indigo-500/70" />
                            ) : activeStation === "clinic" ? (
                              <Scale className="h-7 w-7 text-emerald-600/70" />
                            ) : (
                              <SearchCheck className="h-7 w-7 text-rose-600/70" />
                            )}
                          </div>
                          <h3 className="text-sm font-bold text-slate-700">
                            {activeStation === "socratic"
                              ? "Estación de Taller Socrático en Espera"
                              : activeStation === "clinic"
                                ? "Estación de Consultorio Clínico en Espera"
                                : "Auditor de Integridad en Espera"}
                          </h3>
                          <p className="text-xs text-slate-400 mt-1 max-w-sm">
                            {activeStation === "socratic"
                              ? "Selecciona un caso de ejemplo o ingresa hechos y tu postura para evaluar tu criterio dogmático."
                              : activeStation === "clinic"
                                ? "Carga la narración del usuario para validar competencia por cuantía y generar la ficha docente."
                                : "Pega el texto del documento que deseas verificar contra las relatorías oficiales colombianas."}
                          </p>
                        </div>
                      )}
                    </CardContent>

                    {/* PANEL PERMANENTE DE DOBLE FIRMA Y VISTO BUENO (LEY 2113 & LEY 527) */}
                    {(activeStation === "socratic" || activeStation === "clinic") && (
                      <CardFooter className="p-3.5 sm:p-4 bg-slate-50/90 border-t border-slate-200 flex flex-col gap-2.5 shrink-0">
                        <div className="flex items-center justify-between w-full border-b border-slate-200/80 pb-1.5">
                          <span className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                            <PenTool className="h-3.5 w-3.5 text-indigo-600" />
                            <span>
                              {activeStation === "socratic"
                                ? "Suscripción de Criterio Dogmático (Ley 2113 Art. 9)"
                                : "Suscripción Oficial y Visto Bueno (Ley 2113 de 2021 & Ley 527 de 1999)"}
                            </span>
                          </span>
                          <Badge variant="outline" className="text-[10px] bg-white text-slate-700">
                            {activeStation === "socratic" ? "Acreditación Formativa" : "Doble Suscripción Obligatoria"}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full text-xs">
                          {/* Firma Practicante */}
                          <div className="bg-white p-2.5 rounded-lg border border-slate-200 flex flex-col justify-between shadow-2xs">
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-bold text-slate-800 text-[11px]">Practicante Adscrito</span>
                                {stationStudentSignature ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] py-0">✓ Firmado</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-700 border-amber-300 text-[9px] py-0">Pendiente</Badge>
                                )}
                              </div>
                              <p className="text-[10.5px] text-slate-500 mb-1.5">{user?.user_metadata?.full_name || "Juan Camilo Pérez"} • C.C. 1.020.890.123</p>
                              {stationStudentSignature && (
                                <div className="border border-slate-100 rounded p-1 mb-1.5 bg-slate-50/50 flex justify-center">
                                  <img src={stationStudentSignature} alt="Firma Estudiante" className="h-9 object-contain" />
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setStationSignRole("estudiante");
                                setStationSignContext(activeStation === "socratic" ? "socratic" : "clinic");
                                setIsStationSignOpen(true);
                              }}
                              className="h-6 text-[11px] font-bold w-full bg-white hover:bg-slate-50"
                            >
                              <PenTool className="h-3 w-3 mr-1" />
                              <span>{stationStudentSignature ? "Cambiar Firma Practicante" : "Firmar como Practicante"}</span>
                            </Button>
                          </div>

                          {/* Firma Docente Tutor */}
                          <div className="bg-white p-2.5 rounded-lg border border-slate-200 flex flex-col justify-between shadow-2xs">
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-bold text-slate-800 text-[11px]">Docente Asesor (Vo.Bo.)</span>
                                {stationTutorSignature ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] py-0">✓ Vo.Bo. Acreditado</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-700 border-amber-300 text-[9px] py-0">Sin Visto Bueno</Badge>
                                )}
                              </div>
                              <p className="text-[10.5px] text-slate-500 mb-1.5">Dra. Helena Morales • T.P. 182.934 C.S.J.</p>
                              {stationTutorSignature && (
                                <div className="border border-slate-100 rounded p-1 mb-1.5 bg-slate-50/50 flex justify-center">
                                  <img src={stationTutorSignature} alt="Firma Docente" className="h-9 object-contain" />
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setStationSignRole("docente");
                                setStationSignContext(activeStation === "socratic" ? "socratic" : "clinic");
                                setIsStationSignOpen(true);
                              }}
                              className="h-6 text-[11px] font-bold w-full text-indigo-700 border-indigo-200 bg-white hover:bg-indigo-50"
                            >
                              <PenTool className="h-3 w-3 mr-1" />
                              <span>{stationTutorSignature ? "Cambiar Firma Docente" : "Firmar y Otorgar Vo.Bo."}</span>
                            </Button>
                          </div>
                        </div>
                      </CardFooter>
                    )}
                  </Card>
                </div>

              </div>
            ))}

          {/* MODAL DE CARPETA DE CASO CLÍNICO & PEDAGÓGICO */}
          <ExpedienteClinicoModal
            isOpen={!!selectedProcessForExpediente}
            proceso={selectedProcessForExpediente}
            initialRole={currentView === "docente" || currentView === "director" ? "docente" : "estudiante"}
            onClose={() => setSelectedProcessForExpediente(null)}
          />

          {/* MODAL DE PROYECCIÓN Y EDICIÓN DE BORRADOR JURÍDICO */}
          <Dialog open={isLegalDraftModalOpen} onOpenChange={setIsLegalDraftModalOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-white">
              <DialogHeader className="p-4 sm:p-6 border-b border-slate-100 bg-slate-50/70 shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-emerald-100 text-emerald-800">
                      <ScrollText className="h-5 w-5" />
                    </div>
                    <div>
                      <DialogTitle className="text-base font-bold text-slate-900">
                        Borrador Jurídico Proyectado — Consultorio Ley 2113 de 2021
                      </DialogTitle>
                      <DialogDescription className="text-xs text-slate-500 mt-0.5">
                        {clinicCategory} — Parte: {clinicClaimant || "Asistido por Consultorio"}
                      </DialogDescription>
                    </div>
                  </div>
                  <Badge className="bg-emerald-600 text-white text-[10px]">
                    Pre-Borrador Judicial
                  </Badge>
                </div>
              </DialogHeader>

              <div className="p-4 sm:p-6 flex-1 overflow-y-auto custom-scrollbar flex flex-col min-h-0 space-y-4">
                <Tabs value={draftEditMode} onValueChange={(val: any) => setDraftEditMode(val)} className="w-full flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3 shrink-0">
                    <TabsList className="grid grid-cols-2 w-[240px] h-8 bg-slate-100">
                      <TabsTrigger value="preview" className="text-xs font-semibold">Vista Previa</TabsTrigger>
                      <TabsTrigger value="edit" className="text-xs font-semibold">Editor de Texto</TabsTrigger>
                    </TabsList>
                    <div className="text-xs text-slate-500 hidden sm:block">
                      Ley 2213/2022 • Notificaciones electrónicas incluidas
                    </div>
                  </div>

                  <TabsContent value="preview" className="flex-1 mt-0">
                    <div className="p-5 rounded-xl border border-slate-200 bg-slate-50/50 prose prose-sm max-w-none text-slate-800 text-xs sm:text-[13px] leading-relaxed font-sans">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {stripFollowUpQuestions(legalDraftOutput) || "Sin contenido proyectado."}
                      </ReactMarkdown>
                    </div>
                  </TabsContent>

                  <TabsContent value="edit" className="flex-1 mt-0">
                    <Textarea
                      value={legalDraftOutput || ""}
                      onChange={(e) => setLegalDraftOutput(e.target.value)}
                      rows={18}
                      className="w-full font-mono text-xs leading-relaxed p-4 border-slate-300 focus:border-emerald-500 rounded-xl"
                      placeholder="Modifica o complementa el memorial forense antes de su descarga..."
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-2 shrink-0">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(legalDraftOutput || "")}
                    className="text-xs h-8"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-600 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                    <span>Copiar Escrito</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToDocx(`Demanda_${clinicCategory}_${clinicClaimant || "Consultorio"}`, legalDraftOutput || "", "clinic")}
                    disabled={exportingDocx}
                    className="text-xs h-8 font-semibold text-blue-700 border-blue-200 hover:bg-blue-50"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    <span>Descargar Word (.docx)</span>
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleSendDraftForReview}
                    disabled={isSubmittingVoBo}
                    className="text-xs h-8 bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center gap-1.5 shadow-sm"
                  >
                    {isSubmittingVoBo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    <span>Remitir a Docente para Vo.Bo.</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsLegalDraftModalOpen(false)}
                    className="text-xs h-8 text-slate-500"
                  >
                    Cerrar
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* MODAL DE MATRÍCULA DE ESTUDIANTES Y PROFESORES */}
          <Dialog open={isUserModalOpen} onOpenChange={setIsUserModalOpen}>
            <DialogContent className="sm:max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-indigo-600" />
                  <span>Matrícula Institucional — Censo SICAJ / Ley 2113</span>
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Habilita practicantes, docentes asesores y directivos en el registro oficial del Consultorio Jurídico.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {/* Selector de Rol */}
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Rol Institucional:</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setNewUserRole("estudiante")}
                      className={cn(
                        "p-2 rounded-lg text-xs font-bold border transition-all text-center",
                        newUserRole === "estudiante"
                          ? "bg-emerald-50 border-emerald-400 text-emerald-900 shadow-2xs"
                          : "bg-slate-50 border-slate-200 text-slate-600"
                      )}
                    >
                      Estudiante Practicante
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewUserRole("docente_tutor")}
                      className={cn(
                        "p-2 rounded-lg text-xs font-bold border transition-all text-center",
                        newUserRole === "docente_tutor"
                          ? "bg-indigo-50 border-indigo-400 text-indigo-900 shadow-2xs"
                          : "bg-slate-50 border-slate-200 text-slate-600"
                      )}
                    >
                      Docente Asesor
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewUserRole("director_consultorio")}
                      className={cn(
                        "p-2 rounded-lg text-xs font-bold border transition-all text-center",
                        newUserRole === "director_consultorio"
                          ? "bg-purple-50 border-purple-400 text-purple-900 shadow-2xs"
                          : "bg-slate-50 border-slate-200 text-slate-600"
                      )}
                    >
                      Dirección Clínica
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Nombre Completo:</label>
                    <Input
                      placeholder={newUserRole === "estudiante" ? "Ej: Mariana Salazar Cortés" : "Ej: Dra. Camila Restrepo"}
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      {newUserRole === "estudiante" ? "Código Estudiantil / C.C.:" : "Cédula de Ciudadanía:"}
                    </label>
                    <Input
                      placeholder={newUserRole === "estudiante" ? "Ej: Cód. 202210459 / C.C." : "Ej: C.C. 52.891.034"}
                      value={newUserDocId}
                      onChange={(e) => setNewUserDocId(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Correo Institucional (.edu.co):</label>
                  <Input
                    type="email"
                    placeholder="usuario@universidad.edu.co"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Área Clínica Asignada:</label>
                    <select
                      value={newUserArea}
                      onChange={(e) => setNewUserArea(e.target.value)}
                      className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                    >
                      <option value="Laboral">Laboral & Seg. Social</option>
                      <option value="Civil">Civil & Comercial</option>
                      <option value="Familia">Familia & Conciliación</option>
                      <option value="Penal">Penal Querellable</option>
                      <option value="Público">Derecho Público / Tutelas</option>
                    </select>
                  </div>

                  {newUserRole === "estudiante" ? (
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Nivel de Consultorio:</label>
                      <select
                        value={newUserSemestre}
                        onChange={(e) => setNewUserSemestre(e.target.value)}
                        className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                      >
                        <option value="Consultorio I (Semestre VII)">Consultorio I (Semestre VII)</option>
                        <option value="Consultorio II (Semestre VIII)">Consultorio II (Semestre VIII)</option>
                        <option value="Consultorio III (Semestre IX)">Consultorio III (Semestre IX)</option>
                        <option value="Consultorio IV (Semestre X)">Consultorio IV (Semestre X)</option>
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Tarjeta Profesional CSJ:</label>
                      <Input
                        placeholder="T.P. 189.432 CSJ"
                        value={newUserTP}
                        onChange={(e) => setNewUserTP(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  )}
                </div>

                {newUserRole === "estudiante" && (
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Docente Asesor Supervisor (Ley 2113):</label>
                    <select
                      value={newUserAssignedTutor}
                      onChange={(e) => setNewUserAssignedTutor(e.target.value)}
                      className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                    >
                      {campusUsers
                        .filter((u: any) => u.role === "docente_tutor" || u.role === "director_consultorio")
                        .map((doc: any) => (
                          <option key={doc.id} value={doc.full_name}>
                            {doc.full_name} ({doc.area || "General"})
                          </option>
                        ))}
                      <option value="Dra. Helena Morales">Dra. Helena Morales (Coordinación Clínica)</option>
                    </select>
                  </div>
                )}
              </div>

              <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setIsUserModalOpen(false)} className="text-xs h-8">
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveCampusUser}
                  disabled={isRegisteringUser}
                  className="text-xs h-8 bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center gap-1.5 shadow-sm"
                >
                  {isRegisteringUser ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                  <span>Registrar en Consultorio</span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* DIÁLOGO MODAL DE CAPTURA DE FIRMAS EN WORKSTATION (LEY 2113 & LEY 527) */}
          <Dialog open={isStationSignOpen} onOpenChange={setIsStationSignOpen}>
            <DialogContent className="sm:max-w-lg rounded-2xl p-5 bg-white">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="p-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                    <PenTool className="h-4 w-4" />
                  </span>
                  <DialogTitle className="text-sm font-bold text-slate-900">
                    {stationSignRole === "docente"
                      ? "Suscripción y Visto Bueno Oficial Docente (Ley 2113)"
                      : stationSignContext === "socratic"
                      ? "Certificación de Taller Socrático — Estudiante"
                      : "Suscripción de Dictamen Clínico — Estudiante Practicante"}
                  </DialogTitle>
                </div>
                <DialogDescription className="text-xs text-slate-500">
                  {stationSignRole === "docente"
                    ? "Su firma y número de Tarjeta Profesional certificarán la validez procesal de este asunto conforme a la Ley 2113 de 2021 y Ley 527 de 1999."
                    : "Capture su firma autógrafa o estampe su sello de autoría para formalizar esta actuación en el Consultorio Jurídico."}
                </DialogDescription>
              </DialogHeader>

              {/* Selector de Modo de Firma */}
              <div className="flex bg-slate-100 p-1 rounded-xl text-xs gap-1 my-2">
                <button
                  type="button"
                  onClick={() => setStationSignMode("canvas")}
                  className={`flex-1 py-1.5 rounded-lg font-bold transition-all ${
                    stationSignMode === "canvas" ? "bg-white text-indigo-700 shadow-2xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Firma Manuscrita (Canvas)
                </button>
                <button
                  type="button"
                  onClick={() => setStationSignMode("stamp")}
                  className={`flex-1 py-1.5 rounded-lg font-bold transition-all ${
                    stationSignMode === "stamp" ? "bg-white text-indigo-700 shadow-2xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Sello Criptográfico Ley 527
                </button>
              </div>

              {stationSignMode === "canvas" ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>Dibuje su firma con el mouse o pantalla táctil:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px]">Tinta:</span>
                      <button
                        type="button"
                        onClick={() => setStationPenColor("#000000")}
                        className={`w-4 h-4 rounded-full bg-black ${stationPenColor === "#000000" ? "ring-2 ring-indigo-500" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setStationPenColor("#063970")}
                        className={`w-4 h-4 rounded-full bg-[#063970] ${stationPenColor === "#063970" ? "ring-2 ring-indigo-500" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => stationPadInstance.current?.clear()}
                        className="text-xs text-rose-600 hover:underline font-bold ml-1"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>

                  <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-inner h-40 relative">
                    <canvas
                      ref={stationPadCanvasRef}
                      className="w-full h-full cursor-crosshair touch-none"
                    />
                    <div className="absolute bottom-1 right-2 pointer-events-none text-[9.5px] text-slate-400 font-mono">
                      Área de Suscripción Oficial
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/50 space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-indigo-950 font-bold">
                    <FileCheck className="h-4 w-4 text-emerald-600" />
                    <span>Sello Digital de Integridad Probatoria</span>
                  </div>
                  <div className="text-[11px] text-slate-600 space-y-1">
                    <p><strong>Firmante:</strong> {stationSignRole === "docente" ? "Dra. Helena Morales" : (user?.user_metadata?.full_name || "Juan Camilo Pérez")}</p>
                    <p><strong>Identificación:</strong> {stationSignRole === "docente" ? "T.P. 182.934 C.S.J." : "C.C. 1.020.890.123"}</p>
                    <p><strong>Marco Normativo:</strong> Ley 527 de 1999 & Ley 2113 de 2021</p>
                    <p className="font-mono text-[9.5px] text-slate-500">
                      Sello de Tiempo: {new Date().toISOString().replace("T", " ").slice(0, 19)} UTC
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2 sm:gap-0 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsStationSignOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveStationSignature}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  <span>
                    {stationSignRole === "docente"
                      ? "Firmar y Otorgar Visto Bueno"
                      : "Confirmar y Estampar Firma"}
                  </span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* DIÁLOGO MODAL: SOLICITAR CITA / RESOLVER DUDAS CON PROFESOR O ASISTENTE */}
          <Dialog open={isAppointmentModalOpen} onOpenChange={setIsAppointmentModalOpen}>
            <DialogContent className="sm:max-w-md bg-white rounded-2xl p-5">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200">
                    <CalendarPlus className="h-4 w-4" />
                  </span>
                  <DialogTitle className="text-base font-bold text-slate-900">
                    Solicitar Cita de Asesoría / Resolver Dudas
                  </DialogTitle>
                </div>
                <DialogDescription className="text-xs text-slate-500">
                  Si tienes inquietudes sobre el trámite, competencia o pruebas del caso, agenda una cita o remite tus dudas al docente del área o al asistente de consultorio.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3.5 py-2">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    ¿Con quién deseas solicitar la cita o asesoría?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAppointmentRecipient("docente")}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all text-xs",
                        appointmentRecipient === "docente"
                          ? "border-indigo-600 bg-indigo-50/70 text-indigo-950 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <span className="block font-bold">Docente Asesor</span>
                      <span className="text-[10px] text-slate-500">Profesor especialista de área</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppointmentRecipient("asistente")}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all text-xs",
                        appointmentRecipient === "asistente"
                          ? "border-indigo-600 bg-indigo-50/70 text-indigo-950 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <span className="block font-bold">Asistente de Consultorio</span>
                      <span className="text-[10px] text-slate-500">Apoyo operativo y formal</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Tipo de Duda:</label>
                    <select
                      value={appointmentTopic}
                      onChange={(e: any) => setAppointmentTopic(e.target.value)}
                      className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                    >
                      <option value="procesal">Duda Procesal / Términos</option>
                      <option value="sustantivo">Fondo / Fundamento Jurídico</option>
                      <option value="probatorio">Matriz / Carga Probatoria</option>
                      <option value="formal">Requisitos de Facultad</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Fecha Sugerida:</label>
                    <Input
                      type="date"
                      value={appointmentPreferredDate}
                      onChange={(e) => setAppointmentPreferredDate(e.target.value)}
                      className="text-xs h-8 border-slate-200 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Describe tus dudas o el punto que requieres destrabar:
                  </label>
                  <Textarea
                    placeholder="Ej: Tengo duda de si la reclamación administrativa previa suspendió el término de prescripción del Art. 488 del CST..."
                    value={appointmentDoubtDescription}
                    onChange={(e) => setAppointmentDoubtDescription(e.target.value)}
                    rows={4}
                    className="text-xs resize-none border-slate-200"
                  />
                </div>

                {/* Resumen del expediente vinculado */}
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200/80 text-[11px] text-slate-600 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Expediente:</span>
                    <strong className="text-slate-800">{activeCaseId || "Caso en edición"}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Documentos Completados:</span>
                    <span className="font-bold text-indigo-700">{completedDocsCount} de 5 obligatorios</span>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAppointmentModalOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleRequestAppointment}
                  disabled={isSubmittingAppointment}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
                >
                  {isSubmittingAppointment ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CalendarPlus className="h-3.5 w-3.5 mr-1" />}
                  <span>Solicitar Cita de Asesoría</span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* DIÁLOGO MODAL: REPARTO DE CASO A ESTUDIANTE PRACTICANTE (LEY 2113 DE 2021) */}
          <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
            <DialogContent className="sm:max-w-md bg-white rounded-2xl p-5">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <UserPlus className="h-4 w-4" />
                  </span>
                  <DialogTitle className="text-base font-bold text-slate-900">
                    Reparto de Caso a Practicante Adscrito
                  </DialogTitle>
                </div>
                <DialogDescription className="text-xs text-slate-500">
                  Como Docente Asesor del Área, selecciona el estudiante que asumirá la representación clínica y el diligenciamiento de los 5 documentos de facultad.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3.5 py-2">
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200/80 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Expediente:</span>
                    <strong className="text-slate-800">{caseForAssignment?.radicado || caseForAssignment?.case_number}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Partes / Asunto:</span>
                    <span className="font-medium text-slate-700 text-right">{caseForAssignment?.parties || caseForAssignment?.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Área / Docente Asesor:</span>
                    <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
                      {AREA_PROFESSORS[caseForAssignment?.area || caseForAssignment?.category || "Laboral"]?.name || "Dra. Helena Morales"}
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Seleccionar Estudiante Practicante del Turno:
                  </label>
                  <select
                    value={targetStudentId}
                    onChange={(e) => setTargetStudentId(e.target.value)}
                    className="w-full h-9 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                  >
                    <option value="">-- Elige un estudiante practicante matriculado --</option>
                    {campusUsers
                      .filter((u: any) => u.role === "estudiante")
                      .map((st: any) => (
                        <option key={st.id} value={st.id}>
                          {st.full_name} ({st.semestre || "Semestre X"}) — {st.horas_practica || 0} hrs acreditadas
                        </option>
                      ))}
                  </select>
                </div>

                <div className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200 text-[11px] text-amber-900 space-y-1">
                  <strong>Obligaciones del practicante bajo la Ley 2113:</strong>
                  <p className="text-[10.5px] leading-relaxed">
                    Al asignar este caso, el estudiante deberá diligenciar los 5 documentos de la facultad (entrevista, matriz probatoria, viabilidad, memorial y poder) y podrá solicitarte citas de asesoría si tiene dudas.
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAssignModalOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAssignCaseToStudent}
                  disabled={!targetStudentId}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  <span>Confirmar Reparto a Estudiante</span>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* DIÁLOGO MODAL: CENTRO DE CONCILIACIÓN EXTRAJUDICIAL EN DERECHO (LEY 2220 DE 2022) */}
          <Dialog open={isConciliacionModalOpen} onOpenChange={setIsConciliacionModalOpen}>
            <DialogContent className="max-w-2xl bg-white rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <Scale className="h-4 w-4" />
                  </span>
                  <div>
                    <DialogTitle className="text-base font-bold text-slate-900">
                      Centro de Conciliación — Ley 2220 de 2022
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                      Trámite de Audiencia Extrajudicial en Derecho, emisión de Actas con mérito ejecutivo y constancias de procedibilidad.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-3.5 py-2">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Resultado / Tipo de Documento a Emitir:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setConciliacionType("acuerdo");
                        setConciliacionOutput(null);
                      }}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all text-xs",
                        conciliacionType === "acuerdo"
                          ? "border-emerald-600 bg-emerald-50 text-emerald-950 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <span className="block font-bold">Acta de Acuerdo Total</span>
                      <span className="text-[10px] text-emerald-700">Mérito ejecutivo</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConciliacionType("no_acuerdo");
                        setConciliacionOutput(null);
                      }}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all text-xs",
                        conciliacionType === "no_acuerdo"
                          ? "border-amber-600 bg-amber-50 text-amber-950 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <span className="block font-bold">Imposibilidad de Acuerdo</span>
                      <span className="text-[10px] text-amber-700">Requisito procedibilidad</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConciliacionType("inasistencia");
                        setConciliacionOutput(null);
                      }}
                      className={cn(
                        "p-2.5 rounded-xl border text-left transition-all text-xs",
                        conciliacionType === "inasistencia"
                          ? "border-rose-600 bg-rose-50 text-rose-950 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      )}
                    >
                      <span className="block font-bold">Constancia Inasistencia</span>
                      <span className="text-[10px] text-rose-700">Sanción procesal</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Fecha de Celebración de Audiencia:</label>
                    <Input
                      type="date"
                      value={conciliacionHearingDate}
                      onChange={(e) => setConciliacionHearingDate(e.target.value)}
                      className="text-xs h-8 border-slate-200 bg-white"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Partes Registradas:</label>
                    <div className="h-8 px-2 rounded-md border border-slate-200 bg-slate-50 flex items-center text-[11px] text-slate-700 truncate">
                      {clinicClaimant || "Convocante"} vs. {clinicCounterparty || "Convocado"}
                    </div>
                  </div>
                </div>

                {conciliacionType === "acuerdo" && (
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Fórmulas de Arreglo y Obligaciones Pactadas:
                    </label>
                    <Textarea
                      placeholder="Ej: El convocado se compromete a pagar la suma de $1.500.000 COP en 3 cuotas mensuales de $500.000 los días 5 de cada mes..."
                      value={conciliacionAgreements}
                      onChange={(e) => setConciliacionAgreements(e.target.value)}
                      rows={3}
                      className="text-xs resize-none border-slate-200"
                    />
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleGenerateConciliacionDoc}
                    disabled={isGeneratingConciliacion}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                  >
                    {isGeneratingConciliacion ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Scale className="h-3.5 w-3.5 mr-1.5" />}
                    <span>Proyectar Acta Oficial Ley 2220</span>
                  </Button>
                </div>

                {conciliacionOutput && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-800">Texto Formal del Documento:</span>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(conciliacionOutput)}
                          className="h-6 text-[10.5px] px-2 text-slate-600"
                        >
                          {copied ? <Check className="h-3 w-3 text-emerald-600 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                          <span>{copied ? "Copiado" : "Copiar"}</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => exportToDocx("Acta_o_Constancia_Conciliacion_Ley_2220", conciliacionOutput, true)}
                          className="h-6 text-[10.5px] px-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          <span>Descargar Word (.docx)</span>
                        </Button>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg max-h-56 overflow-y-auto text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed">
                      {conciliacionOutput}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsConciliacionModalOpen(false)}
                  className="text-xs"
                >
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* DIÁLOGO MODAL: CALCULADORA DE TÉRMINOS JUDICIALES EN DÍAS HÁBILES (COLOMBIA) */}
          <Dialog open={isTermCalculatorOpen} onOpenChange={setIsTermCalculatorOpen}>
            <DialogContent className="sm:max-w-lg bg-white rounded-2xl p-5">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="p-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200">
                    <Timer className="h-4 w-4" />
                  </span>
                  <div>
                    <DialogTitle className="text-base font-bold text-slate-900">
                      Calculadora de Términos Judiciales (Días Hábiles)
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                      Cómputo determinista conforme al CGP, CST, CPACA, Ley 2213 de 2022 y festivos oficiales de Colombia.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-3.5 py-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Fecha de Notificación / Auto:</label>
                    <Input
                      type="date"
                      value={termStartDate}
                      onChange={(e) => setTermStartDate(e.target.value)}
                      className="text-xs h-8 border-slate-200 bg-white"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Forma de Notificación:</label>
                    <select
                      value={termNotifyType}
                      onChange={(e: any) => setTermNotifyType(e.target.value)}
                      className="w-full h-8 text-xs rounded-md border border-slate-200 px-2 bg-white font-medium text-slate-800"
                    >
                      <option value="estado">Por Estado Electrónico (Art. 295 CGP)</option>
                      <option value="personal">Personal / Estrados</option>
                      <option value="aviso">Por Aviso (+3 días)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Presets de Términos Habituales en Consultorio:
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setTermBusinessDays(3)}
                      className={cn(
                        "p-2 rounded-lg border text-left transition-all text-xs",
                        termBusinessDays === 3 ? "border-indigo-600 bg-indigo-50 text-indigo-900 font-bold" : "border-slate-200 text-slate-600"
                      )}
                    >
                      <span className="block font-bold">3 Días</span>
                      <span className="text-[9.5px] text-slate-500">Reposición / Aclaración</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTermBusinessDays(5)}
                      className={cn(
                        "p-2 rounded-lg border text-left transition-all text-xs",
                        termBusinessDays === 5 ? "border-indigo-600 bg-indigo-50 text-indigo-900 font-bold" : "border-slate-200 text-slate-600"
                      )}
                    >
                      <span className="block font-bold">5 Días</span>
                      <span className="text-[9.5px] text-slate-500">Subsanar Demanda</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTermBusinessDays(10)}
                      className={cn(
                        "p-2 rounded-lg border text-left transition-all text-xs",
                        termBusinessDays === 10 ? "border-indigo-600 bg-indigo-50 text-indigo-900 font-bold" : "border-slate-200 text-slate-600"
                      )}
                    >
                      <span className="block font-bold">10 Días</span>
                      <span className="text-[9.5px] text-slate-500">Excepciones Verbal Sum.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTermBusinessDays(20)}
                      className={cn(
                        "p-2 rounded-lg border text-left transition-all text-xs",
                        termBusinessDays === 20 ? "border-indigo-600 bg-indigo-50 text-indigo-900 font-bold" : "border-slate-200 text-slate-600"
                      )}
                    >
                      <span className="block font-bold">20 Días</span>
                      <span className="text-[9.5px] text-slate-500">Contestación Ordinario</span>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">Días Hábiles personalizados:</span>
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={termBusinessDays}
                      onChange={(e) => setTermBusinessDays(parseInt(e.target.value) || 1)}
                      className="w-16 h-8 text-xs font-bold text-center"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCalculateLegalTerm}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
                  >
                    <Timer className="h-3.5 w-3.5 mr-1" />
                    <span>Calcular Vencimiento</span>
                  </Button>
                </div>

                {calculatedTermResult && (
                  <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-700">Fecha Límite de Vencimiento:</span>
                      <span className="font-mono font-black text-sm text-slate-900 bg-white px-2.5 py-0.5 rounded border border-slate-200">
                        {calculatedTermResult.dueDate}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Plazo Restante:</span>
                      <Badge className={cn(
                        "text-xs font-bold",
                        calculatedTermResult.status === "danger"
                          ? "bg-rose-100 text-rose-800 border-rose-300"
                          : calculatedTermResult.status === "warning"
                            ? "bg-amber-100 text-amber-800 border-amber-300"
                            : "bg-emerald-100 text-emerald-800 border-emerald-300"
                      )}>
                        {calculatedTermResult.remainingDays <= 0
                          ? "¡Vence hoy!"
                          : `${calculatedTermResult.remainingDays} días restantes`}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed border-t border-slate-200/60 pt-2">
                      {calculatedTermResult.summary} (Excluyendo fines de semana y festivos oficiales de Colombia).
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsTermCalculatorOpen(false)}
                  className="text-xs"
                >
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
